import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { authService, dbService, getAppId, getInitialAuthToken, getAllUsers, deleteUserAccount } from '../services/firebase';
import { getInitialMonthlyPL } from '../config/monthlyPLAccounts';
import { getPendingAssessment, clearPendingAssessment } from '../utils/freeAssessmentStorage';
import { getPendingStartupChecklist, clearPendingStartupChecklist } from '../utils/freeStartupChecklistStorage';

// Action Types
export const ActionTypes = {
  // Auth actions
  SET_LOADING: 'SET_LOADING',
  SET_AUTHENTICATED: 'SET_AUTHENTICATED',
  SET_USER_ID: 'SET_USER_ID',
  SET_USER: 'SET_USER',
  
  // UI actions
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SHOW_MESSAGE: 'SHOW_MESSAGE',
  HIDE_MESSAGE: 'HIDE_MESSAGE',
  SHOW_WELCOME_MESSAGE: 'SHOW_WELCOME_MESSAGE',
  HIDE_WELCOME_MESSAGE: 'HIDE_WELCOME_MESSAGE',
  
  // Data actions
  UPDATE_BUSINESS_PLAN: 'UPDATE_BUSINESS_PLAN',
  UPDATE_FINANCIAL_DATA: 'UPDATE_FINANCIAL_DATA',
  SET_VENDORS: 'SET_VENDORS',
  ADD_VENDOR: 'ADD_VENDOR',
  UPDATE_VENDOR: 'UPDATE_VENDOR',
  REMOVE_VENDOR: 'REMOVE_VENDOR',
  UPDATE_CERTIFICATIONS: 'UPDATE_CERTIFICATIONS',
  
  // Draft management actions
  SET_DRAFTS: 'SET_DRAFTS',
  SET_CURRENT_DRAFT_ID: 'SET_CURRENT_DRAFT_ID',
  CREATE_DRAFT: 'CREATE_DRAFT',
  CREATE_DRAFT_FROM_IMPORT: 'CREATE_DRAFT_FROM_IMPORT',
  UPDATE_DRAFT: 'UPDATE_DRAFT',
  DELETE_DRAFT: 'DELETE_DRAFT',
  DUPLICATE_DRAFT: 'DUPLICATE_DRAFT',
  SET_DRAFT_COMPARISON: 'SET_DRAFT_COMPARISON',
  UPDATE_PROJECT_TIMELINE: 'UPDATE_PROJECT_TIMELINE',
  UPDATE_OPENING_PLAN_PROGRESS: 'UPDATE_OPENING_PLAN_PROGRESS',
  UPDATE_DOCUMENT_VERSIONS: 'UPDATE_DOCUMENT_VERSIONS',
  UPDATE_SOPS: 'UPDATE_SOPS',
  UPDATE_LEDGER_ENTRIES: 'UPDATE_LEDGER_ENTRIES',
  UPDATE_EQUIPMENT_DATA: 'UPDATE_EQUIPMENT_DATA',
  MARK_NOTIFICATIONS_READ: 'MARK_NOTIFICATIONS_READ',
  
  // Auto-save actions
  SET_SAVE_STATUS: 'SET_SAVE_STATUS',
  SET_LAST_SAVED_AT: 'SET_LAST_SAVED_AT'
};

// Initial state
const initialState = {
  // Auth state
  isLoading: false,
  isAuthenticated: false,
  userId: null,
  user: null,
  subscription: {
    status: 'active',
    plan: 'free',
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    stripeCustomerId: null,
    stripeSubscriptionId: null
  },
  
  // UI state
  activeTab: 'concept-pitch',
  showMessage: false,
  message: {
    type: 'info', // 'success', 'error', 'info'
    title: '',
    text: ''
  },
  showWelcomeMessage: false,
  
  // Auto-save state
  saveStatus: 'idle', // 'idle', 'saving', 'saved', 'error'
  lastSavedAt: null,
  
  // Draft management state
  drafts: [],
  currentDraftId: null,
  draftComparison: {
    isVisible: false,
    draftIds: []
  },
  
  // Current draft data (legacy compatibility)
  businessPlan: {
    ideation: {
      businessConcept: '',
      coreInspiration: '',
      targetAudience: '',
      initialProblem: '',
      solutionIdea: '',
      differentiator: '',
      businessType: '',
      location: '',
      timeCommitment: '',
      investmentLevel: '',
      successMeasure: '',
      personalMotivation: '',
      marketOpportunity: '',
      resourcesNeeded: '',
      riskAssessment: ''
    },
    elevatorPitch: {
      template: 'problem-solution',
      hook: '',
      problem: '',
      solution: '',
      uniqueValue: '',
      target: '',
      traction: '',
      ask: '',
      finalPitch: '',
      practiceNotes: ''
    },
    executiveSummary: {
      businessName: '',
      businessType: '',
      location: '',
      fundingRequest: '',
      missionStatement: '',
      keySuccessFactors: '',
      financialSummary: ''
    },
    marketAnalysis: {
      industryOverview: '',
      targetMarket: '',
      marketSize: '',
      competitiveAnalysis: '',
      marketTrends: '',
      customerDemographics: ''
    },
    operationsPlan: {
      location: '',
      facilityRequirements: '',
      hoursOfOperation: '',
      staffingPlan: '',
      supplierRelationships: '',
      qualityControl: ''
    },
    managementTeam: {
      keyPersonnel: '',
      organizationalStructure: '',
      advisors: '',
      compensationPlan: ''
    },
    serviceDescription: {
      productsServices: '',
      uniqueSellingProposition: '',
      pricingStrategy: '',
      lifecycle: ''
    },
    marketingStrategy: {
      marketingMix: '',
      salesStrategy: '',
      customerAcquisition: '',
      brandingStrategy: '',
      digitalMarketing: ''
    }
  },
  
  financialData: {
    // Restaurant open date (for monthly statements)
    restaurantOpenDate: null,
    // Monthly actual expenses (keyed by 'YYYY-MM')
    monthlyActuals: {},
    // Restaurant Operations
    restaurantOperations: {
      seats: 50,
      hoursOfOperation: {
        monday: { open: '11:00', close: '22:00', closed: false },
        tuesday: { open: '11:00', close: '22:00', closed: false },
        wednesday: { open: '11:00', close: '22:00', closed: false },
        thursday: { open: '11:00', close: '22:00', closed: false },
        friday: { open: '11:00', close: '23:00', closed: false },
        saturday: { open: '10:00', close: '23:00', closed: false },
        sunday: { open: '10:00', close: '21:00', closed: false }
      },
      averageCheck: {
        lunch: 18,
        dinner: 32,
        brunch: 24,
        beverages: 8
      },
      tableTurnover: {
        lunch: 1.5,
        dinner: 2.0,
        brunch: 2.5,
        average: 2.0
      },
      occupancyRate: {
        lunch: 0.7,
        dinner: 0.9,
        brunch: 0.8,
        average: 0.8
      },
      serviceTypes: {
        lunch: true,
        dinner: true,
        brunch: false
      }
    },
    revenue: {
      foodSales: 0,
      beverageSales: 0,
      merchandiseSales: 0,
      cateringSales: 0,
      otherRevenue: 0
    },
    cogs: {
      foodCogsPercent: 0.28,
      beverageCogsPercent: 0.22,
      merchandiseCogsPercent: 0.15,
      cateringCogsPercent: 0.32,
      otherCogsPercent: 0.12
    },
    operatingExpenses: {
      // Core Operating Expenses
      rent: 0,
      utilities: 0,
      insurance: 0,
      marketing: 0,
      legalAccounting: 0,
      repairsMaintenance: 0,
      supplies: 0,
      adminOffice: 0,
      otherOperatingExpenses: 0,
      
      // Detailed Operational Expenses
      linenService: 0,
      trashCollection: 0,
      compostService: 0,
      cleaningService: 0,
      hoodCleaning: 0,
      greaseTrapCleaning: 0,
      internetPhone: 0,
      securitySystem: 0,
      pestControl: 0,
      wasteManagement: 0,
      equipmentMaintenance: 0,
      uniformService: 0,
      dishwareReplacement: 0,
      cleaningSupplies: 0,
      paperGoods: 0,
      kitchenSupplies: 0,
      pointOfSale: 0,
      creditCardProcessing: 0,
      bankFees: 0,
      permitsLicenses: 0,
      musicLicensing: 0,
      deliveryServiceFees: 0,
      trainingCertification: 0,
      employeeBenefits: 0,
      workersCompensation: 0,
      unemploymentInsurance: 0,
      healthInsurance: 0,
      retirementBenefits: 0,
      uniformLaundry: 0,
      parkingFees: 0,
      storageFees: 0,
      professionalServices: 0,
      technologySupport: 0,
      softwareSubscriptions: 0,
      inventoryManagement: 0,
      qualityControl: 0,
      safetyTraining: 0,
      equipmentRental: 0,
      temporaryStaffing: 0,
      consulting: 0,
      travelEntertainment: 0,
      charitableDonations: 0,
      localTaxes: 0,
      propertyTaxes: 0,
      businessTaxes: 0,
      depreciation: 0,
      amortization: 0,
      interestExpense: 0,
      loanFees: 0,
      vehicleExpenses: 0,
      menuPrinting: 0,
      websiteMaintenance: 0,
      emailMarketing: 0,
      socialMediaManagement: 0,
      photography: 0,
      reservationSystem: 0,
      giftCardFees: 0,
      thirdPartyDeliveryFees: 0,
      employeeMeals: 0,
      smallwares: 0,
      eventHosting: 0,
      
      // Detailed Labor Structure - Individual Positions
      labor: {
        // Management Positions
        management: [
          { id: 'gm-1', name: 'General Manager', hourlyRate: 28, hoursPerWeek: 50, tips: 0, benefits: true, department: 'Management' },
          { id: 'am-1', name: 'Assistant Manager', hourlyRate: 22, hoursPerWeek: 45, tips: 0, benefits: true, department: 'Management' },
          { id: 'km-1', name: 'Kitchen Manager', hourlyRate: 25, hoursPerWeek: 50, tips: 0, benefits: true, department: 'Management' }
        ],
        // Front of House Positions
        frontOfHouse: [
          { id: 'server-1', name: 'Server 1', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
          { id: 'server-2', name: 'Server 2', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
          { id: 'server-3', name: 'Server 3', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
          { id: 'server-4', name: 'Server 4', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
          { id: 'server-5', name: 'Server 5', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
          { id: 'server-6', name: 'Server 6', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
          { id: 'bartender-1', name: 'Bartender 1', hourlyRate: 15, hoursPerWeek: 35, tips: 15, benefits: false, department: 'FOH' },
          { id: 'bartender-2', name: 'Bartender 2', hourlyRate: 15, hoursPerWeek: 35, tips: 15, benefits: false, department: 'FOH' },
          { id: 'host-1', name: 'Host 1', hourlyRate: 15, hoursPerWeek: 25, tips: 3, benefits: false, department: 'FOH' },
          { id: 'host-2', name: 'Host 2', hourlyRate: 15, hoursPerWeek: 25, tips: 3, benefits: false, department: 'FOH' },
          { id: 'busser-1', name: 'Busser 1', hourlyRate: 15, hoursPerWeek: 30, tips: 4, benefits: false, department: 'FOH' },
          { id: 'busser-2', name: 'Busser 2', hourlyRate: 15, hoursPerWeek: 30, tips: 4, benefits: false, department: 'FOH' },
          { id: 'cashier-1', name: 'Cashier 1', hourlyRate: 15, hoursPerWeek: 20, tips: 0, benefits: false, department: 'FOH' }
        ],
        // Back of House Positions
        backOfHouse: [
          { id: 'chef-1', name: 'Head Chef', hourlyRate: 32, hoursPerWeek: 55, tips: 0, benefits: true, department: 'BOH' },
          { id: 'cook-1', name: 'Cook 1', hourlyRate: 22, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
          { id: 'cook-2', name: 'Cook 2', hourlyRate: 20, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
          { id: 'cook-3', name: 'Cook 3', hourlyRate: 20, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
          { id: 'cook-4', name: 'Cook 4', hourlyRate: 18, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
          { id: 'prep-1', name: 'Prep Cook 1', hourlyRate: 18, hoursPerWeek: 35, tips: 0, benefits: false, department: 'BOH' },
          { id: 'prep-2', name: 'Prep Cook 2', hourlyRate: 16, hoursPerWeek: 35, tips: 0, benefits: false, department: 'BOH' },
          { id: 'dish-1', name: 'Dishwasher 1', hourlyRate: 15, hoursPerWeek: 30, tips: 0, benefits: false, department: 'BOH' },
          { id: 'dish-2', name: 'Dishwasher 2', hourlyRate: 15, hoursPerWeek: 30, tips: 0, benefits: false, department: 'BOH' }
        ],
        // Support Staff
        support: [
          { id: 'clean-1', name: 'Cleaning Staff 1', hourlyRate: 15, hoursPerWeek: 20, tips: 0, benefits: false, department: 'Support' },
          { id: 'clean-2', name: 'Cleaning Staff 2', hourlyRate: 15, hoursPerWeek: 20, tips: 0, benefits: false, department: 'Support' },
          { id: 'maintenance-1', name: 'Maintenance', hourlyRate: 20, hoursPerWeek: 15, tips: 0, benefits: false, department: 'Support' }
        ],
        // Tax Rates
        taxRates: {
          federalIncomeTax: 0.15,
          stateIncomeTax: 0.05,
          socialSecurity: 0.062,
          medicare: 0.0145,
          unemployment: 0.03,
          workersComp: 0.02,
          benefitsRate: 0.25 // Health insurance, retirement, etc.
        }
      },
      
      // Market Trends & Projections
      marketTrends: {
        // Industry Benchmarks
        industryBenchmarks: {
          avgRevenuePerSeat: 75000, // Annual revenue per seat
          avgFoodCostPercentage: 28, // 28% food cost
          avgLaborPercentage: 32, // 32% labor cost
          avgOccupancyRate: 0.75, // 75% average occupancy
          avgCheckSize: 25, // Average check size
          avgTableTurnover: 2.0 // Tables turned per meal period
        },
        // Growth Projections
        growthProjections: {
          year1: { revenueGrowth: 1.0, costInflation: 1.0 },
          year2: { revenueGrowth: 1.15, costInflation: 1.03 },
          year3: { revenueGrowth: 1.25, costInflation: 1.06 },
          year4: { revenueGrowth: 1.35, costInflation: 1.09 },
          year5: { revenueGrowth: 1.45, costInflation: 1.12 }
        },
        // Seasonal Adjustments
        seasonalFactors: {
          spring: { factor: 1.05, months: [3, 4, 5] },
          summer: { factor: 1.15, months: [6, 7, 8] },
          fall: { factor: 1.08, months: [9, 10, 11] },
          winter: { factor: 0.92, months: [12, 1, 2] }
        }
      },
      
      // Yearly Permits & Compliance Costs
      yearlyPermits: {
        // Business Permits
        businessPermits: {
          businessLicense: 200, // Annual business license
          foodServiceLicense: 150, // Annual food service license
          alcoholLicense: 2500, // Annual alcohol license
          entertainmentLicense: 300, // Music/entertainment license
          outdoorDiningPermit: 500, // Outdoor seating permit
          signagePermit: 150, // Signage permit
          healthDepartmentFee: 300, // Annual health department fee
          fireDepartmentInspection: 200, // Fire safety inspection
          buildingPermitRenewal: 400, // Building permit renewal
          parkingPermit: 1000, // Parking permits
          dumpsterPermit: 800, // Waste disposal permit
          greaseTrapPermit: 300, // Grease trap permit
          waterPermit: 250, // Water/sewer permit
          gasPermit: 200, // Gas line permit
          electricalPermit: 180 // Electrical permit
        },
        // Insurance Requirements
        insuranceCosts: {
          generalLiability: 2500, // Annual general liability
          propertyInsurance: 3500, // Property insurance
          workersCompensation: 4000, // Workers comp
          liquorLiability: 1200, // Liquor liability
          foodContamination: 800, // Food contamination insurance
          businessInterruption: 1500, // Business interruption
          cyberLiability: 600, // Cyber security insurance
          umbrellaPolicy: 2000 // Umbrella coverage
        },
        // Professional Services
        professionalServices: {
          accountingServices: 8000, // Annual accounting
          legalServices: 5000, // Legal services
          taxPreparation: 2000, // Tax preparation
          payrollServices: 2400, // Payroll processing
          bookkeeping: 6000, // Monthly bookkeeping
          consulting: 3000, // Business consulting
          marketingServices: 4000, // Marketing support
          hrServices: 2000 // HR services
        }
      },
      
      // Maintenance & Equipment Costs
      maintenanceCosts: {
        // Equipment Maintenance
        equipmentMaintenance: {
          kitchenEquipment: 8000, // Annual kitchen equipment maintenance
          hvacMaintenance: 3600, // HVAC maintenance (quarterly)
          plumbingMaintenance: 2400, // Plumbing maintenance
          electricalMaintenance: 1800, // Electrical maintenance
          refrigerationMaintenance: 3000, // Refrigeration maintenance
          greaseTrapCleaning: 1200, // Quarterly grease trap cleaning
          hoodCleaning: 2400, // Quarterly hood cleaning
          floorMaintenance: 2000, // Floor cleaning/maintenance
          windowCleaning: 1200, // Window cleaning
          carpetCleaning: 800, // Carpet cleaning
          pestControl: 1800, // Monthly pest control
          securitySystem: 1200, // Security system maintenance
          posSystem: 1200, // POS system maintenance
          fireSuppression: 600, // Fire suppression maintenance
          emergencyEquipment: 400 // Emergency equipment maintenance
        },
        // Building Maintenance
        buildingMaintenance: {
          roofMaintenance: 2000, // Roof maintenance
          exteriorPainting: 3000, // Exterior painting (every 3 years)
          interiorPainting: 1500, // Interior painting (every 2 years)
          doorMaintenance: 800, // Door maintenance
          windowRepair: 1000, // Window repair/replacement
          parkingLotMaintenance: 1500, // Parking lot maintenance
          landscaping: 2400, // Landscaping maintenance
          snowRemoval: 1800, // Snow removal (seasonal)
          sidewalkMaintenance: 600, // Sidewalk maintenance
          fenceMaintenance: 400, // Fence maintenance
          awningMaintenance: 600, // Awning maintenance
          signageMaintenance: 800 // Signage maintenance
        },
        // Equipment Replacement Schedule
        equipmentReplacement: {
          kitchenEquipment: { cost: 50000, years: 7, annualReserve: 7143 },
          hvacSystem: { cost: 25000, years: 10, annualReserve: 2500 },
          refrigeration: { cost: 30000, years: 8, annualReserve: 3750 },
          furniture: { cost: 15000, years: 5, annualReserve: 3000 },
          electronics: { cost: 10000, years: 3, annualReserve: 3333 },
          flooring: { cost: 20000, years: 8, annualReserve: 2500 },
          lighting: { cost: 8000, years: 5, annualReserve: 1600 },
          plumbing: { cost: 12000, years: 10, annualReserve: 1200 }
        }
      },
      
      // Legacy Payroll (for backward compatibility)
      salaryOwners: 0,
      salaryFullTime: 0,
      salaryPartTime: 0,
      payrollTaxRate: 0.12
    },
    
    // Funding Sources
    fundingSources: {
      // Personal Investment
      personalSavings: 0,
      personalAssets: 0,
      homeEquity: 0,
      retirementFunds: 0,
      
      // Family & Friends
      familyLoans: 0,
      friendLoans: 0,
      familyGifts: 0,
      
      // Traditional Financing
      bankLoans: 0,
      sbaLoans: 0,
      equipmentFinancing: 0,
      lineOfCredit: 0,
      businessCreditCards: 0,
      
      // Alternative Financing
      crowdfunding: 0,
      angelInvestors: 0,
      ventureCapital: 0,
      grants: 0,
      privateInvestors: 0,
      
      // Other Sources
      businessPartners: 0,
      supplierCredit: 0,
      otherSources: 0,
      
      // Funding Terms
      fundingTerms: {
        interestRates: {
          bankLoans: 6.5, // %
          sbaLoans: 4.5, // %
          equipmentFinancing: 8.0, // %
          lineOfCredit: 7.5, // %
          businessCreditCards: 18.0, // %
          familyLoans: 3.0, // %
          friendLoans: 5.0 // %
        },
        repaymentTerms: {
          bankLoans: 60, // months
          sbaLoans: 84, // months
          equipmentFinancing: 36, // months
          lineOfCredit: 24, // months
          familyLoans: 120, // months
          friendLoans: 60 // months
        },
        collateral: {
          personalGuarantee: true,
          businessAssets: true,
          realEstate: false,
          equipment: true
        }
      }
    },
    
    startupCosts: {
      leaseholdImprovements: 0,
      kitchenEquipment: 0,
      furnitureFixtures: 0,
      initialInventory: 0,
      preOpeningSalaries: 0,
      depositsLicenses: 0,
      initialMarketing: 0,
      contingency: 0,
      signage: 0,
      posHardware: 0,
      smallwaresStartup: 0,
      openingBeverageInventory: 0,
      trainingPreOpen: 0,
      securityInstall: 0,
      hoodVentilation: 0,
      plumbingElectrical: 0,
      professionalFees: 0,
      preOpeningRent: 0,
      otherStartup: 0,
      // Fixed assets (balance sheet)
      realEstateLand: 0,
      realEstateBuildings: 0,
      equipment: 0,
      vehicles: 0,
      otherFixedAssets: 0,
      // Operating capital (balance sheet)
      prepaidInsurance: 0,
      rentDeposits: 0,
      utilityDeposits: 0,
      supplies: 0,
      workingCapitalCashOnHand: 0
    },
    fixedAssetsDepreciation: {
      realEstateLand: null,
      realEstateBuildings: 1,
      leaseholdImprovements: 7,
      equipment: 7,
      furnitureFixtures: 5,
      vehicles: 5,
      other: 5
    },
    startingBalanceSheetMeta: {
      preparedBy: '',
      companyName: ''
    },
    startupCostLineItems: {
      items: [
        { id: 'sc1', category: 'Business Set up', description: 'Fees and licensing', unit: '1', quantity: 1, unitCost: 1000 },
        { id: 'sc2', category: 'Business Set up', description: 'Legal fees licenses', unit: '1', quantity: 1, unitCost: 5000 },
        { id: 'sc3', category: 'Business Set up', description: 'Accounting and invoice setup', unit: '1', quantity: 1, unitCost: 2000 },
        { id: 'sc4', category: 'Business Set up', description: 'LLC formation (one time)', unit: '1', quantity: 1, unitCost: 500 },
        { id: 'sc5', category: 'Business Set up', description: 'Payroll and HR set up', unit: 'per/month', quantity: 1, unitCost: 400 },
        { id: 'sc6', category: 'Business Set up', description: 'Concept design', unit: '1', quantity: 1, unitCost: 5000 },
        { id: 'sc7', category: 'Business Set up', description: 'Marketing, Branding, PR pre opening', unit: '1', quantity: 1, unitCost: 5000 },
        { id: 'sc8', category: 'Business Set up', description: 'Architect fees', unit: '1', quantity: 1, unitCost: 5000 },
        { id: 'sc9', category: 'Equipment and supplies', description: 'Signage', unit: '1', quantity: 1, unitCost: 1000 },
        { id: 'sc10', category: 'Equipment and supplies', description: 'POS System (install)', unit: '1', quantity: 1, unitCost: 690 },
        { id: 'sc11', category: 'Equipment and supplies', description: 'Kitchen equipment hotline and major appliances', unit: '1', quantity: 1, unitCost: 15000 },
        { id: 'sc12', category: 'Equipment and supplies', description: 'Upstairs refrigeration', unit: '1', quantity: 2, unitCost: 1100 },
        { id: 'sc13', category: 'Equipment and supplies', description: 'Ice machine', unit: '1', quantity: 1, unitCost: 1500 },
        { id: 'sc14', category: 'Equipment and supplies', description: 'Downstairs chest freezers', unit: '1', quantity: 1, unitCost: 600 },
        { id: 'sc15', category: 'Equipment and supplies', description: 'Keg beverage coolers (3 door)', unit: '1', quantity: 2, unitCost: 800 },
        { id: 'sc16', category: 'Equipment and supplies', description: 'Equipment', unit: '1', quantity: 2, unitCost: 800 },
        { id: 'sc17', category: 'Equipment and supplies', description: 'Sound system', unit: '1', quantity: 1, unitCost: 1000 },
        { id: 'sc18', category: 'Equipment and supplies', description: 'Water filtration (Blue Drop install)', unit: '1', quantity: 1, unitCost: 700 },
        { id: 'sc19', category: 'COGS', description: 'Food inventory', unit: '1', quantity: 1, unitCost: 1000 },
        { id: 'sc20', category: 'COGS', description: 'Non alcoholic bev', unit: '1', quantity: 1, unitCost: 1000 },
        { id: 'sc21', category: 'COGS', description: 'Paper goods', unit: '1', quantity: 1, unitCost: 200 },
        { id: 'sc22', category: 'COGS', description: 'Branded Cups', unit: '1', quantity: 1, unitCost: 1000 },
        { id: 'sc23', category: 'COGS', description: 'Branded Merch.', unit: '1', quantity: 1, unitCost: 1000 },
        { id: 'sc24', category: 'COGS', description: 'Training labor', unit: 'hours', quantity: 30, unitCost: 20 },
        { id: 'sc25', category: 'COGS', description: 'Consultants - Beverage', unit: '1', quantity: 1, unitCost: 2000 },
        { id: 'sc26', category: 'Construction build out', description: 'Construction admin', unit: '1', quantity: 1, unitCost: 35000 },
        { id: 'sc27', category: 'Construction build out', description: 'Laborer', unit: '1', quantity: 1, unitCost: 4000 },
        { id: 'sc28', category: 'Construction build out', description: 'Testing', unit: '1', quantity: 1, unitCost: 1200 },
        { id: 'sc29', category: 'Construction build out', description: 'Insurance', unit: '1', quantity: 1, unitCost: 3000 },
        { id: 'sc30', category: 'Construction build out', description: 'Building Permit', unit: '1', quantity: 1, unitCost: 3300 },
        { id: 'sc31', category: 'Construction build out', description: 'Rough Carpentry Labor', unit: '1', quantity: 1, unitCost: 6500 },
        { id: 'sc32', category: 'Construction build out', description: 'Finish Carpentry Labor', unit: '1', quantity: 1, unitCost: 7000 },
        { id: 'sc33', category: 'Construction build out', description: 'Cabinetry', unit: '1', quantity: 1, unitCost: 5000 },
        { id: 'sc34', category: 'Construction build out', description: 'Door Installation', unit: '1', quantity: 1, unitCost: 2000 },
        { id: 'sc35', category: 'Construction build out', description: 'Hood system', unit: '1', quantity: 1, unitCost: 40000 },
        { id: 'sc36', category: 'Construction build out', description: 'Walk in cooler', unit: '1', quantity: 1, unitCost: 8000 },
        { id: 'sc37', category: 'Construction build out', description: 'Plumbing Labor', unit: '1', quantity: 1, unitCost: 23000 },
        { id: 'sc38', category: 'Construction build out', description: 'Fire Protection', unit: '1', quantity: 1, unitCost: 6000 },
        { id: 'sc39', category: 'Construction build out', description: 'HVAC Labor', unit: '1', quantity: 1, unitCost: 12000 },
        { id: 'sc40', category: 'Construction build out', description: 'Electrical Labor', unit: '1', quantity: 1, unitCost: 20000 },
        { id: 'sc41', category: 'Construction build out', description: 'Fire Alarm', unit: '1', quantity: 1, unitCost: 6000 }
      ]
    },
    // Vendor linkage for P&L line items (vendor id per expense key)
    expenseVendors: {
      operating: {},
      startup: {}
    },
    // Monthly P&L estimates by chart-of-accounts (section id -> account code -> monthly amount)
    monthlyPL: getInitialMonthlyPL(),
    restaurantDetails: {
      location: 'Boston',
      seats: 50,
      squareFootage: 2000,
      operatingHours: '11:00 AM - 10:00 PM',
      daysOpen: 7
    },
    restaurantType: {
      type: 'new', // 'new' or 'existing'
      buildCosts: 0, // For new restaurants
      purchasePrice: 0, // For existing restaurants
      renovations: 0, // Renovation costs if needed
      needsRenovations: false // Whether renovations are required
    },
    // Cap table: ownership / equity (managers, employees, founders, investors)
    capTable: {
      entries: [] // { id, name, role, type: 'common'|'preferred', ownershipPercent, shares?, source: 'manual'|'management'|'labor' }
    }
  },
  
  vendors: [],
  certifications: [],
  projectTimeline: null,
  openingPlanProgress: { completedTaskIds: [] },
  documentVersions: [],
  sops: [],
  ledgerEntries: [],
  equipmentData: null, // { bidSheet, customEquipment, plateStyles, selectedItems, budget }
  notificationReadIds: []
};

// Project intent options for new projects (opening new, buying existing, helping existing)
export const PROJECT_INTENTS = {
  OPENING_NEW: 'opening_new',
  BUYING_EXISTING: 'buying_existing',
  HELPING_EXISTING: 'helping_existing'
};

// Draft helper functions
const createNewDraft = (name = `Draft ${Date.now()}`, baseDraft = null, projectIntent = null, enabledFeatures = null, initialLocation = null) => {
  const now = new Date();
  const fromBase = !!baseDraft;

  let businessPlan = fromBase ? { ...baseDraft.businessPlan } : { ...initialState.businessPlan };
  let financialData = fromBase ? { ...baseDraft.financialData } : { ...initialState.financialData };

  if (initialLocation && !fromBase) {
    financialData = {
      ...financialData,
      restaurantDetails: {
        ...financialData.restaurantDetails,
        location: initialLocation
      }
    };
    businessPlan = {
      ...businessPlan,
      executiveSummary: {
        ...businessPlan.executiveSummary,
        location: initialLocation
      }
    };
  }

  return {
    id: `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    projectIntent: projectIntent ?? baseDraft?.projectIntent ?? null,
    enabledFeatures: enabledFeatures ?? baseDraft?.enabledFeatures ?? null,
    createdAt: now,
    updatedAt: now,
    isActive: false,
    businessPlan,
    financialData,
    vendors: baseDraft ? [...baseDraft.vendors] : [],
    openingPlanProgress: baseDraft?.openingPlanProgress ? { ...baseDraft.openingPlanProgress, completedTaskIds: [...(baseDraft.openingPlanProgress.completedTaskIds || [])] } : { completedTaskIds: [] },
    documentVersions: baseDraft?.documentVersions ? [...baseDraft.documentVersions] : [],
    equipmentData: baseDraft?.equipmentData ? JSON.parse(JSON.stringify(baseDraft.equipmentData)) : null,
    ledgerEntries: Array.isArray(baseDraft?.ledgerEntries) ? [...baseDraft.ledgerEntries] : [],
    sops: baseDraft?.sops ? baseDraft.sops.map(s => ({ ...s, steps: [...(s.steps || [])] })) : [],
    freeAssessment: baseDraft?.freeAssessment || null,
    startupChecklist: baseDraft?.startupChecklist || null
  };
};

/** Create a draft from imported pending data (assessment or startup checklist) */
const createDraftFromPendingImport = (name, overrides = {}) => {
  const draft = createNewDraft(name);
  if (overrides.freeAssessment) draft.freeAssessment = overrides.freeAssessment;
  if (overrides.startupChecklist) draft.startupChecklist = overrides.startupChecklist;
  return draft;
};

// Sample Boston restaurant drafts for demonstration
const createSampleDrafts = () => {
  const now = new Date();
  const hour = 60 * 60 * 1000;
  
  return [
        {
      id: 'demo_north_end_italian',
      name: 'North End Italian Bistro',
      createdAt: new Date(now.getTime() - 5 * hour),
      updatedAt: new Date(now.getTime() - 1 * hour),
      concept: 'North End Italian',
      conceptId: 'concept_north_end_italian',
      conceptDescription: 'Authentic Northern Italian cuisine in Boston\'s historic North End',
      businessPlan: {
         ideation: {
           businessConcept: 'A family-style Italian restaurant in Boston\'s North End that serves authentic Northern Italian cuisine to locals and tourists because there\'s a lack of true regional Italian specialties.',
           coreInspiration: 'Growing up with my Nonna\'s authentic Northern Italian recipes and seeing tourists getting generic "Italian-American" food instead of real Italian cuisine',
           targetAudience: 'Food-loving tourists, local Italian-Americans, and professionals seeking authentic dining experiences',
           initialProblem: 'Most North End restaurants serve generic Italian-American food rather than authentic regional specialties, leaving both tourists and locals wanting real Italian cuisine',
           solutionIdea: 'Authentic Northern Italian recipes, imported ingredients, traditional cooking methods, and warm family atmosphere',
           differentiator: 'Only restaurant in North End serving authentic Northern Italian cuisine with family recipes passed down for generations',
           businessType: 'restaurant',
           location: 'north-end',
           timeCommitment: 'intensive',
           investmentLevel: 'high',
           successMeasure: 'Building a beloved neighborhood institution that preserves Italian culinary heritage while providing financial stability for my family',
           personalMotivation: 'Honoring my grandmother\'s legacy and sharing authentic Italian culture with Boston',
           marketOpportunity: 'Food sales, wine pairings, cooking classes, private events, catering',
           resourcesNeeded: 'Family recipes, Italian cooking experience, connections with Italian importers, culinary training',
           riskAssessment: 'High competition in North End, seasonal tourist fluctuations, high Boston rent, staff retention challenges'
         },
         elevatorPitch: {
           template: 'problem-solution',
           hook: 'Did you know that despite having over 100 Italian restaurants, Boston\'s North End doesn\'t have a single authentic Northern Italian restaurant?',
           problem: 'Tourists and locals alike are stuck eating generic Italian-American food instead of experiencing real regional Italian cuisine',
           solution: 'We\'re opening Nonna\'s Table to serve authentic Northern Italian specialties using recipes passed down through generations of my family',
           uniqueValue: 'We\'re the only restaurant bringing true Northern Italian traditions to Boston, with imported ingredients and time-honored cooking methods',
           target: 'Food-loving tourists, local Italian-Americans, and Boston professionals who appreciate authentic dining experiences',
           traction: 'Validated through family catering events and positive feedback from Italian community leaders',
           ask: 'We\'re seeking $485,000 in funding to bring authentic Italian heritage to Boston\'s most historic neighborhood',
           finalPitch: '',
           practiceNotes: ''
         },
         executiveSummary: {
           businessName: 'Nonna\'s Table',
          businessType: 'casual-dining',
          location: 'north-end',
          fundingRequest: '$485,000',
          missionStatement: 'To bring authentic Northern Italian cuisine to Boston\'s historic North End, combining traditional family recipes with modern dining experiences.',
          keySuccessFactors: '• Prime North End location with high tourist traffic\n• Authentic family recipes from Northern Italy\n• Experienced Italian chef with 15+ years\n• Strong relationships with local Italian suppliers\n• Outdoor patio seating for 20 guests',
          financialSummary: '• Projected Year 1 Revenue: $875,000\n• Target Gross Margin: 68%\n• Break-even Timeline: 18 months\n• Initial Investment: $485,000\n• Projected ROI: 18% by Year 3'
        },
        marketAnalysis: {
          industryOverview: 'Boston\'s North End is a premier dining destination with over 100 restaurants. Italian cuisine represents 35% of the area\'s dining options.',
          targetMarket: 'Tourists (40%), local professionals (35%), and North End residents (25%). Average age 25-55, household income $75K+.',
          marketSize: 'North End dining market: $125M annually. Italian cuisine segment: $44M. Targeting 0.7% market share.',
          competitiveAnalysis: 'Key competitors: Giacomo\'s, Bricco, Prezza. Differentiation through authentic Northern Italian focus and family atmosphere.',
          marketTrends: 'Growing demand for authentic ethnic cuisine, farm-to-table ingredients, and experiential dining.',
          customerDemographics: 'Primary: Couples and small groups, ages 30-60, income $75K+. Secondary: Tourists seeking authentic Italian experience.'
        },
        operationsPlan: {},
        managementTeam: {},
        serviceDescription: {},
        marketingStrategy: {}
      },
      financialData: {
        restaurantOperations: {
          seats: 60,
          hoursOfOperation: {
            monday: { open: '11:00', close: '22:00', closed: false },
            tuesday: { open: '11:00', close: '22:00', closed: false },
            wednesday: { open: '11:00', close: '22:00', closed: false },
            thursday: { open: '11:00', close: '22:00', closed: false },
            friday: { open: '11:00', close: '23:00', closed: false },
            saturday: { open: '10:00', close: '23:00', closed: false },
            sunday: { open: '10:00', close: '21:00', closed: false }
          },
          averageCheck: {
            lunch: 22,
            dinner: 38,
            brunch: 28,
            beverages: 12
          },
          tableTurnover: {
            lunch: 1.8,
            dinner: 2.2,
            brunch: 2.8,
            average: 2.3
          },
          occupancyRate: {
            lunch: 0.75,
            dinner: 0.95,
            brunch: 0.85,
            average: 0.85
          }
        },
        revenue: {
          foodSales: 700000,
          beverageSales: 175000,
          merchandiseSales: 0,
          cateringSales: 0,
          otherRevenue: 0
        },
        cogs: {
          foodCogsPercent: 0.28,
          beverageCogsPercent: 0.22,
          merchandiseCogsPercent: 0.15,
          cateringCogsPercent: 0.32,
          otherCogsPercent: 0.12
        },
        operatingExpenses: {
          rent: 135000,
          utilities: 24000,
          insurance: 18000,
          marketing: 30000,
          legalAccounting: 12000,
          repairsMaintenance: 15000,
          supplies: 0,
          adminOffice: 0,
          otherOperatingExpenses: 0,
          // Detailed operational expenses
          linenService: 3600,
          trashCollection: 2400,
          compostService: 1800,
          cleaningService: 12000,
          hoodCleaning: 2400,
          greaseTrapCleaning: 1200,
          internetPhone: 3600,
          securitySystem: 2400,
          pestControl: 1800,
          wasteManagement: 2400,
          equipmentMaintenance: 6000,
          uniformService: 2400,
          dishwareReplacement: 3000,
          cleaningSupplies: 4800,
          paperGoods: 3600,
          kitchenSupplies: 6000,
          pointOfSale: 4800,
          creditCardProcessing: 15000,
          bankFees: 1200,
          permitsLicenses: 3000,
          musicLicensing: 1200,
          deliveryServiceFees: 15000,
          trainingCertification: 3000,
          employeeBenefits: 15000,
          workersCompensation: 8000,
          unemploymentInsurance: 4000,
          healthInsurance: 24000,
          retirementBenefits: 12000,
          uniformLaundry: 1800,
          parkingFees: 3600,
          storageFees: 0,
          professionalServices: 6000,
          technologySupport: 1800,
          softwareSubscriptions: 2400,
          inventoryManagement: 1800,
          qualityControl: 3000,
          safetyTraining: 2400,
          equipmentRental: 0,
          temporaryStaffing: 0,
          consulting: 5000,
          travelEntertainment: 3000,
          charitableDonations: 2000,
          localTaxes: 5000,
          propertyTaxes: 8000,
          businessTaxes: 12000,
          
          // Detailed Labor Structure - Individual Positions
          labor: {
            // Management Positions
            management: [
              { id: 'gm-1', name: 'General Manager', hourlyRate: 28, hoursPerWeek: 50, tips: 0, benefits: true, department: 'Management' },
              { id: 'am-1', name: 'Assistant Manager', hourlyRate: 22, hoursPerWeek: 45, tips: 0, benefits: true, department: 'Management' },
              { id: 'km-1', name: 'Kitchen Manager', hourlyRate: 25, hoursPerWeek: 50, tips: 0, benefits: true, department: 'Management' }
            ],
            // Front of House Positions
            frontOfHouse: [
              { id: 'server-1', name: 'Server 1', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
              { id: 'server-2', name: 'Server 2', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
              { id: 'server-3', name: 'Server 3', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
              { id: 'server-4', name: 'Server 4', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
              { id: 'server-5', name: 'Server 5', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
              { id: 'server-6', name: 'Server 6', hourlyRate: 15, hoursPerWeek: 35, tips: 10, benefits: false, department: 'FOH' },
              { id: 'bartender-1', name: 'Bartender 1', hourlyRate: 15, hoursPerWeek: 35, tips: 15, benefits: false, department: 'FOH' },
              { id: 'bartender-2', name: 'Bartender 2', hourlyRate: 15, hoursPerWeek: 35, tips: 15, benefits: false, department: 'FOH' },
              { id: 'host-1', name: 'Host 1', hourlyRate: 15, hoursPerWeek: 25, tips: 3, benefits: false, department: 'FOH' },
              { id: 'host-2', name: 'Host 2', hourlyRate: 15, hoursPerWeek: 25, tips: 3, benefits: false, department: 'FOH' },
              { id: 'busser-1', name: 'Busser 1', hourlyRate: 15, hoursPerWeek: 30, tips: 4, benefits: false, department: 'FOH' },
              { id: 'busser-2', name: 'Busser 2', hourlyRate: 15, hoursPerWeek: 30, tips: 4, benefits: false, department: 'FOH' },
              { id: 'cashier-1', name: 'Cashier 1', hourlyRate: 15, hoursPerWeek: 20, tips: 0, benefits: false, department: 'FOH' }
            ],
            // Back of House Positions
            backOfHouse: [
              { id: 'chef-1', name: 'Head Chef', hourlyRate: 32, hoursPerWeek: 55, tips: 0, benefits: true, department: 'BOH' },
              { id: 'cook-1', name: 'Cook 1', hourlyRate: 22, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
              { id: 'cook-2', name: 'Cook 2', hourlyRate: 20, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
              { id: 'cook-3', name: 'Cook 3', hourlyRate: 20, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
              { id: 'cook-4', name: 'Cook 4', hourlyRate: 18, hoursPerWeek: 40, tips: 0, benefits: false, department: 'BOH' },
              { id: 'prep-1', name: 'Prep Cook 1', hourlyRate: 18, hoursPerWeek: 35, tips: 0, benefits: false, department: 'BOH' },
              { id: 'prep-2', name: 'Prep Cook 2', hourlyRate: 16, hoursPerWeek: 35, tips: 0, benefits: false, department: 'BOH' },
              { id: 'dish-1', name: 'Dishwasher 1', hourlyRate: 15, hoursPerWeek: 30, tips: 0, benefits: false, department: 'BOH' },
              { id: 'dish-2', name: 'Dishwasher 2', hourlyRate: 15, hoursPerWeek: 30, tips: 0, benefits: false, department: 'BOH' }
            ],
            // Support Staff
            support: [
              { id: 'clean-1', name: 'Cleaning Staff 1', hourlyRate: 15, hoursPerWeek: 20, tips: 0, benefits: false, department: 'Support' },
              { id: 'clean-2', name: 'Cleaning Staff 2', hourlyRate: 15, hoursPerWeek: 20, tips: 0, benefits: false, department: 'Support' },
              { id: 'maintenance-1', name: 'Maintenance', hourlyRate: 20, hoursPerWeek: 15, tips: 0, benefits: false, department: 'Support' }
            ],
            // Tax Rates
            taxRates: {
              federalIncomeTax: 0.15,
              stateIncomeTax: 0.05,
              socialSecurity: 0.062,
              medicare: 0.0145,
              unemployment: 0.03,
              workersComp: 0.02,
              benefitsRate: 0.25 // Health insurance, retirement, etc.
            }
          },
          
          // Market Trends & Projections
          marketTrends: {
            // Industry Benchmarks
            industryBenchmarks: {
              avgRevenuePerSeat: 80000, // Annual revenue per seat
              avgFoodCostPercentage: 28, // 28% food cost
              avgLaborPercentage: 32, // 32% labor cost
              avgOccupancyRate: 0.75, // 75% average occupancy
              avgCheckSize: 28, // Average check size
              avgTableTurnover: 2.0 // Tables turned per meal period
            },
            // Growth Projections
            growthProjections: {
              year1: { revenueGrowth: 1.0, costInflation: 1.0 },
              year2: { revenueGrowth: 1.15, costInflation: 1.03 },
              year3: { revenueGrowth: 1.25, costInflation: 1.06 },
              year4: { revenueGrowth: 1.35, costInflation: 1.09 },
              year5: { revenueGrowth: 1.45, costInflation: 1.12 }
            },
            // Seasonal Adjustments
            seasonalFactors: {
              spring: { factor: 1.05, months: [3, 4, 5] },
              summer: { factor: 1.15, months: [6, 7, 8] },
              fall: { factor: 1.08, months: [9, 10, 11] },
              winter: { factor: 0.92, months: [12, 1, 2] }
            }
          },
          
          // Yearly Permits & Compliance Costs
          yearlyPermits: {
            // Business Permits
            businessPermits: {
              businessLicense: 200, // Annual business license
              foodServiceLicense: 150, // Annual food service license
              alcoholLicense: 2500, // Annual alcohol license
              entertainmentLicense: 300, // Music/entertainment license
              outdoorDiningPermit: 500, // Outdoor seating permit
              signagePermit: 150, // Signage permit
              healthDepartmentFee: 300, // Annual health department fee
              fireDepartmentInspection: 200, // Fire safety inspection
              buildingPermitRenewal: 400, // Building permit renewal
              parkingPermit: 1000, // Parking permits
              dumpsterPermit: 800, // Waste disposal permit
              greaseTrapPermit: 300, // Grease trap permit
              waterPermit: 250, // Water/sewer permit
              gasPermit: 200, // Gas line permit
              electricalPermit: 180 // Electrical permit
            },
            // Insurance Requirements
            insuranceCosts: {
              generalLiability: 2500, // Annual general liability
              propertyInsurance: 3500, // Property insurance
              workersCompensation: 4000, // Workers comp
              liquorLiability: 1200, // Liquor liability
              foodContamination: 800, // Food contamination insurance
              businessInterruption: 1500, // Business interruption
              cyberLiability: 600, // Cyber security insurance
              umbrellaPolicy: 2000 // Umbrella coverage
            },
            // Professional Services
            professionalServices: {
              accountingServices: 8000, // Annual accounting
              legalServices: 5000, // Legal services
              taxPreparation: 2000, // Tax preparation
              payrollServices: 2400, // Payroll processing
              bookkeeping: 6000, // Monthly bookkeeping
              consulting: 3000, // Business consulting
              marketingServices: 4000, // Marketing support
              hrServices: 2000 // HR services
            }
          },
          
          // Maintenance & Equipment Costs
          maintenanceCosts: {
            // Equipment Maintenance
            equipmentMaintenance: {
              kitchenEquipment: 8000, // Annual kitchen equipment maintenance
              hvacMaintenance: 3600, // HVAC maintenance (quarterly)
              plumbingMaintenance: 2400, // Plumbing maintenance
              electricalMaintenance: 1800, // Electrical maintenance
              refrigerationMaintenance: 3000, // Refrigeration maintenance
              greaseTrapCleaning: 1200, // Quarterly grease trap cleaning
              hoodCleaning: 2400, // Quarterly hood cleaning
              floorMaintenance: 2000, // Floor cleaning/maintenance
              windowCleaning: 1200, // Window cleaning
              carpetCleaning: 800, // Carpet cleaning
              pestControl: 1800, // Monthly pest control
              securitySystem: 1200, // Security system maintenance
              posSystem: 1200, // POS system maintenance
              fireSuppression: 600, // Fire suppression maintenance
              emergencyEquipment: 400 // Emergency equipment maintenance
            },
            // Building Maintenance
            buildingMaintenance: {
              roofMaintenance: 2000, // Roof maintenance
              exteriorPainting: 3000, // Exterior painting (every 3 years)
              interiorPainting: 1500, // Interior painting (every 2 years)
              doorMaintenance: 800, // Door maintenance
              windowRepair: 1000, // Window repair/replacement
              parkingLotMaintenance: 1500, // Parking lot maintenance
              landscaping: 2400, // Landscaping maintenance
              snowRemoval: 1800, // Snow removal (seasonal)
              sidewalkMaintenance: 600, // Sidewalk maintenance
              fenceMaintenance: 400, // Fence maintenance
              awningMaintenance: 600, // Awning maintenance
              signageMaintenance: 800 // Signage maintenance
            },
            // Equipment Replacement Schedule
            equipmentReplacement: {
              kitchenEquipment: { cost: 50000, years: 7, annualReserve: 7143 },
              hvacSystem: { cost: 25000, years: 10, annualReserve: 2500 },
              refrigeration: { cost: 30000, years: 8, annualReserve: 3750 },
              furniture: { cost: 15000, years: 5, annualReserve: 3000 },
              electronics: { cost: 10000, years: 3, annualReserve: 3333 },
              flooring: { cost: 20000, years: 8, annualReserve: 2500 },
              lighting: { cost: 8000, years: 5, annualReserve: 1600 },
              plumbing: { cost: 12000, years: 10, annualReserve: 1200 }
            }
          },
          
          salaryOwners: 85000,
          salaryFullTime: 200000,
          salaryPartTime: 140000,
          payrollTaxRate: 0.12
        },
        
        // Funding Sources
        fundingSources: {
          // Personal Investment
          personalSavings: 150000,
          personalAssets: 25000,
          homeEquity: 0,
          retirementFunds: 0,
          
          // Family & Friends
          familyLoans: 50000,
          friendLoans: 0,
          familyGifts: 25000,
          
          // Traditional Financing
          bankLoans: 200000,
          sbaLoans: 150000,
          equipmentFinancing: 100000,
          lineOfCredit: 50000,
          businessCreditCards: 25000,
          
          // Alternative Financing
          crowdfunding: 0,
          angelInvestors: 0,
          ventureCapital: 0,
          grants: 10000,
          privateInvestors: 100000,
          
          // Other Sources
          businessPartners: 75000,
          supplierCredit: 15000,
          otherSources: 0,
          
          // Funding Terms
          fundingTerms: {
            interestRates: {
              bankLoans: 6.5, // %
              sbaLoans: 4.5, // %
              equipmentFinancing: 8.0, // %
              lineOfCredit: 7.5, // %
              businessCreditCards: 18.0, // %
              familyLoans: 3.0, // %
              friendLoans: 5.0 // %
            },
            repaymentTerms: {
              bankLoans: 60, // months
              sbaLoans: 84, // months
              equipmentFinancing: 36, // months
              lineOfCredit: 24, // months
              familyLoans: 120, // months
              friendLoans: 60 // months
            },
            collateral: {
              personalGuarantee: true,
              businessAssets: true,
              realEstate: false,
              equipment: true
            }
          }
        },
        
        startupCosts: {
          leaseholdImprovements: 200000,
          kitchenEquipment: 145000,
          furnitureFixtures: 55000,
          initialInventory: 18000,
          preOpeningSalaries: 0,
          depositsLicenses: 30000,
          initialMarketing: 20000,
          contingency: 17000
        }
      },
      vendors: [
        { id: 1, name: "Tedd Rama", company: "Baldor Foods", email: "bbweborders@baldorfoods.com", category: "Food Supplier", priority: "high" },
        { id: 2, name: "Todd Evens", company: "Sysco Foods", email: "Todd.Evans@sysco.com", category: "Food Supplier", priority: "high" }
      ]
    },
    {
      id: 'demo_north_end_italian_variation',
      name: 'North End Italian Bistro - Premium Concept',
      createdAt: new Date(now.getTime() - 4 * hour),
      updatedAt: new Date(now.getTime() - 2 * hour),
      concept: 'North End Italian',
      conceptId: 'concept_north_end_italian',
      conceptDescription: 'Authentic Northern Italian cuisine in Boston\'s historic North End',
      businessPlan: {
        ideation: {
          businessConcept: 'A premium Italian restaurant in Boston\'s North End that serves authentic Northern Italian cuisine with wine pairings and private dining experiences.',
          coreInspiration: 'Creating an upscale dining experience that honors Italian culinary traditions while providing an intimate, sophisticated atmosphere',
          targetAudience: 'Affluent diners, wine enthusiasts, special occasions, business entertainment, and Italian culture lovers',
          initialProblem: 'North End lacks a premium Italian dining experience that combines authentic cuisine with sophisticated service and wine expertise',
          solutionIdea: 'Premium Italian dining with extensive wine list, private dining rooms, chef\'s table experiences, and seasonal tasting menus',
          differentiator: 'Only premium Italian restaurant in North End with dedicated sommelier, private dining, and seasonal tasting menus',
          businessType: 'fine-dining',
          location: 'north-end',
          timeCommitment: 'intensive',
          investmentLevel: 'premium',
          successMeasure: 'Becoming Boston\'s premier Italian fine dining destination with $1.2M annual revenue and wine program recognition',
          personalMotivation: 'Elevating Italian cuisine in Boston while building a world-class dining establishment',
          marketOpportunity: 'Fine dining, wine sales, private events, corporate dining, wine education, seasonal events',
          resourcesNeeded: 'Fine dining experience, wine expertise, premium location, significant capital, exceptional service staff',
          riskAssessment: 'Very high startup costs, premium pricing strategy, need for exceptional staff, longer payback period'
        },
        elevatorPitch: {
          template: 'problem-solution',
          hook: 'Boston\'s North End has 100+ Italian restaurants, but not a single one offers a premium fine dining experience with world-class wine pairings.',
          problem: 'Affluent diners seeking sophisticated Italian dining must travel outside Boston or settle for casual North End options',
          solution: 'We\'re creating Il Ristorante, a premium Italian fine dining establishment with authentic Northern Italian cuisine and an extensive wine program',
          uniqueValue: 'Boston\'s only premium Italian restaurant combining authentic regional cuisine with sophisticated service and world-class wine expertise',
          target: 'Affluent professionals, wine enthusiasts, special occasion diners, and business entertainers seeking premium Italian dining',
          traction: 'Validated through focus groups with 50+ high-income diners and partnerships with Italian wine importers',
          ask: 'We\'re seeking $750,000 to create Boston\'s premier Italian fine dining destination in the historic North End',
          finalPitch: '',
          practiceNotes: ''
        },
        executiveSummary: {
          businessName: 'Il Ristorante',
          businessType: 'fine-dining',
          location: 'north-end',
          fundingRequest: '$750,000',
          missionStatement: 'To provide Boston\'s most sophisticated Italian dining experience, combining authentic Northern Italian cuisine with world-class wine pairings and exceptional service.',
          keySuccessFactors: '• Premium North End location with private dining capabilities\n• Authentic Northern Italian recipes with seasonal ingredients\n• Dedicated sommelier and extensive wine program\n• Private dining rooms and chef\'s table experiences\n• Exceptional service staff with Italian hospitality training',
          financialSummary: '• Projected Year 1 Revenue: $1,200,000\n• Target Gross Margin: 75%\n• Break-even Timeline: 24 months\n• Initial Investment: $750,000\n• Projected ROI: 25% by Year 3'
        },
        marketAnalysis: {
          industryOverview: 'Boston\'s fine dining market is growing 8% annually, with Italian cuisine representing 20% of premium dining preferences.',
          targetMarket: 'Affluent professionals (45%), special occasions (30%), business entertainment (15%), and wine enthusiasts (10%).',
          marketSize: 'Boston fine dining market: $180M annually. Italian fine dining segment: $36M. Targeting 3.3% market share.',
          competitiveAnalysis: 'Competitors: Mamma Maria, Prezza, Bricco. Differentiation through premium wine program and private dining experiences.',
          marketTrends: 'Growing demand for premium dining experiences, wine education, private events, and authentic ethnic cuisine.',
          customerDemographics: 'Primary: High-income professionals, ages 35-65, household income $150K+. Secondary: Wine enthusiasts and special occasion diners.'
        },
        operationsPlan: {},
        managementTeam: {},
        serviceDescription: {},
        marketingStrategy: {}
      },
      financialData: {
        revenue: {
          foodSales: 900000,
          beverageSales: 250000,
          merchandiseSales: 0,
          cateringSales: 50000,
          otherRevenue: 0
        },
        cogs: {
          foodCogsPercent: 0.25,
          beverageCogsPercent: 0.30,
          merchandiseCogsPercent: 0.15,
          cateringCogsPercent: 0.28,
          otherCogsPercent: 0.12
        },
        operatingExpenses: {
          rent: 180000,
          utilities: 30000,
          insurance: 25000,
          marketing: 50000,
          legalAccounting: 15000,
          repairsMaintenance: 20000,
          supplies: 0,
          adminOffice: 0,
          otherOperatingExpenses: 0,
          salaryOwners: 120000,
          salaryFullTime: 300000,
          salaryPartTime: 180000,
          payrollTaxRate: 0.12
        },
        startupCosts: {
          leaseholdImprovements: 300000,
          kitchenEquipment: 200000,
          furnitureFixtures: 100000,
          initialInventory: 25000,
          preOpeningSalaries: 0,
          depositsLicenses: 50000,
          initialMarketing: 40000,
          contingency: 35000
        },
        fundingSources: {
          ownersEquity: 300000,
          investorFunds: 250000,
          bankLoans: 200000,
          otherFunding: 0
        }
      },
      vendors: [
        { id: 1, name: "Tedd Rama", company: "Baldor Foods", email: "bbweborders@baldorfoods.com", category: "Food Supplier", priority: "high" },
        { id: 2, name: "Todd Evens", company: "Sysco Foods", email: "Todd.Evans@sysco.com", category: "Food Supplier", priority: "high" },
        { id: 12, name: "Marco Rossi", company: "Italian Wine Importers", email: "mrossi@italianwine.com", category: "Wine Supplier", priority: "high" }
      ]
    },
        {
      id: 'demo_seaport_fast_casual',
      name: 'Seaport Fast-Casual Concept',
      createdAt: new Date(now.getTime() - 3 * hour),
      updatedAt: new Date(now.getTime() - 30 * 60 * 1000),
      concept: 'Seaport Fast Casual',
      conceptId: 'concept_seaport_fast_casual',
      conceptDescription: 'Healthy fast-casual dining in Boston\'s growing Seaport district',
      businessPlan: {
         ideation: {
           businessConcept: 'A healthy fast-casual restaurant in Boston\'s Seaport that serves customizable bowls to busy tech professionals because there\'s a lack of quick, nutritious options in the area.',
           coreInspiration: 'Working long hours in the tech industry and struggling to find healthy, quick meal options that weren\'t expensive or time-consuming',
           targetAudience: 'Tech workers, young professionals, health-conscious millennials, and convention center visitors',
           initialProblem: 'Seaport professionals are stuck with expensive sit-down restaurants or unhealthy fast food, with limited healthy options for busy schedules',
           solutionIdea: 'Customizable healthy bowls with fresh ingredients, quick service, mobile ordering, and affordable pricing',
           differentiator: 'Focus on tech worker needs: fast WiFi, phone charging stations, healthy brain foods, and efficient mobile ordering',
           businessType: 'fast-casual',
           location: 'seaport',
           timeCommitment: 'full-time',
           investmentLevel: 'medium',
           successMeasure: 'Becoming the go-to lunch spot for Seaport professionals with 500+ daily customers and potential for expansion',
           personalMotivation: 'Solving my own problem while building a scalable business that promotes healthy eating habits',
           marketOpportunity: 'Bowl sales, corporate catering, meal subscriptions, branded merchandise, franchise expansion',
           resourcesNeeded: 'Restaurant management experience, healthy food knowledge, tech-savvy marketing, startup capital',
           riskAssessment: 'High Seaport rents, competition from chains, seasonal business fluctuations, need for tech integration'
         },
         elevatorPitch: {
           template: 'problem-solution',
           hook: 'What if I told you that Seaport tech workers spend an average of $18 and 45 minutes for lunch every day, but 78% say they want healthier, faster options?',
           problem: 'Boston\'s fastest-growing business district lacks affordable, healthy food options for busy professionals',
           solution: 'Harbor Bowl offers customizable, nutritious bowls with 5-minute service and mobile ordering designed specifically for the tech community',
           uniqueValue: 'We\'re the only restaurant combining healthy fast-casual with tech-worker amenities like charging stations and optimized WiFi',
           target: 'The 15,000+ tech workers, young professionals, and convention visitors in Boston\'s Seaport district',
           traction: 'Validated through surveys of 200+ Seaport workers and pre-orders from 3 major tech companies',
           ask: 'We\'re seeking $325,000 to capture Boston\'s fastest-growing market with a scalable, tech-forward healthy dining concept',
           finalPitch: '',
           practiceNotes: ''
         },
         executiveSummary: {
           businessName: 'Harbor Bowl',
          businessType: 'fast-casual',
          location: 'seaport',
          fundingRequest: '$325,000',
          missionStatement: 'To provide fresh, healthy, customizable bowl meals to Boston\'s busy Seaport professionals and residents.',
          keySuccessFactors: '• Modern Seaport location in growing business district\n• Health-focused menu with customizable options\n• Fast service model for busy professionals\n• Sustainable sourcing and eco-friendly packaging\n• Digital ordering and delivery integration',
          financialSummary: '• Projected Year 1 Revenue: $650,000\n• Target Gross Margin: 72%\n• Break-even Timeline: 14 months\n• Initial Investment: $325,000\n• Projected ROI: 22% by Year 3'
        },
        marketAnalysis: {
          industryOverview: 'Fast-casual dining is the fastest growing restaurant segment in Boston, with 12% annual growth.',
          targetMarket: 'Seaport professionals (60%), residents (25%), tourists/convention visitors (15%). Health-conscious, tech-savvy demographics.',
          marketSize: 'Seaport dining market: $85M annually. Fast-casual segment: $25M. Targeting 2.6% market share.',
          competitiveAnalysis: 'Competitors: Sweetgreen, CAVA, Dig Inn. Differentiation through local Boston ingredients and harbor-themed atmosphere.',
          marketTrends: 'Rising demand for healthy fast-food, customization, mobile ordering, and sustainable practices.',
          customerDemographics: 'Primary: Working professionals, ages 25-45, income $85K+. Secondary: Health-conscious millennials and Gen Z.'
        },
        operationsPlan: {},
        managementTeam: {},
        serviceDescription: {},
        marketingStrategy: {}
      },
      financialData: {
        revenue: {
          foodSales: 520000,
          beverageSales: 85000,
          merchandiseSales: 15000,
          cateringSales: 30000,
          otherRevenue: 0
        },
        cogs: {
          foodCogsPercent: 0.26,
          beverageCogsPercent: 0.20,
          merchandiseCogsPercent: 0.40,
          cateringCogsPercent: 0.30,
          otherCogsPercent: 0.12
        },
        operatingExpenses: {
          rent: 96000,
          utilities: 18000,
          insurance: 14000,
          marketing: 35000,
          legalAccounting: 8000,
          repairsMaintenance: 10000,
          supplies: 0,
          adminOffice: 0,
          otherOperatingExpenses: 0,
          salaryOwners: 65000,
          salaryFullTime: 120000,
          salaryPartTime: 85000,
          payrollTaxRate: 0.12
        },
        startupCosts: {
          leaseholdImprovements: 120000,
          kitchenEquipment: 95000,
          furnitureFixtures: 35000,
          initialInventory: 12000,
          preOpeningSalaries: 0,
          depositsLicenses: 25000,
          initialMarketing: 25000,
          contingency: 13000
        },
        fundingSources: {
          ownersEquity: 150000,
          investorFunds: 100000,
          bankLoans: 75000,
          otherFunding: 0
        }
      },
      vendors: [
        { id: 3, name: "Craig Newton", company: "Supplies on the Fly", email: "cnewton@suppliesonthefly.com", category: "Restaurant Supplies", priority: "medium" },
        { id: 8, name: "Amie Raskin", company: "Intelligentsia Coffee Inc", email: "araskin@intelligentsiacoffee.com", category: "Beverage Supplier", priority: "medium" }
      ]
    },
    {
      id: 'demo_back_bay_fine_dining',
      name: 'Back Bay Fine Dining',
      createdAt: new Date(now.getTime() - 7 * hour),
      updatedAt: new Date(now.getTime() - 2 * hour),
             businessPlan: {
         ideation: {
           businessConcept: 'An upscale restaurant in Boston\'s Back Bay that showcases New England ingredients with global techniques to affluent professionals because there\'s demand for sophisticated dining that honors local heritage.',
           coreInspiration: 'Years of fine dining experience and noticing that Boston lacks restaurants that truly celebrate New England\'s culinary heritage with modern techniques',
           targetAudience: 'Affluent professionals, special occasion diners, business entertainers, and culinary enthusiasts',
           initialProblem: 'Back Bay has many upscale restaurants but few that authentically celebrate New England\'s rich culinary heritage with sophisticated modern techniques',
           solutionIdea: 'Seasonal tasting menus featuring local ingredients, expert wine pairings, impeccable service, and intimate private dining experiences',
           differentiator: 'Only fine dining restaurant in Boston exclusively focused on elevated New England cuisine with James Beard-level culinary expertise',
           businessType: 'restaurant',
           location: 'back-bay',
           timeCommitment: 'intensive',
           investmentLevel: 'premium',
           successMeasure: 'Achieving James Beard recognition, becoming Boston\'s premier destination for New England cuisine, and generating $1.5M annual revenue',
           personalMotivation: 'Establishing Boston as a serious culinary destination while building a legacy restaurant that defines New England fine dining',
           marketOpportunity: 'Tasting menu dinners, wine program, private events, corporate dining, culinary classes, cookbook sales',
           resourcesNeeded: 'Fine dining experience, James Beard-level chef network, wine expertise, significant capital, luxury service training',
           riskAssessment: 'Very high startup costs, lengthy payback period, demanding clientele, need for exceptional staff retention'
         },
         elevatorPitch: {
           template: 'problem-solution',
           hook: 'Boston spends $180 million annually on fine dining, but no restaurant has ever won a James Beard Award for celebrating our own New England culinary heritage.',
           problem: 'Despite being a major culinary city, Boston lacks a world-class restaurant that authentically showcases the sophistication of New England cuisine',
           solution: 'Meridian will be Boston\'s first restaurant dedicated to elevating New England ingredients with global techniques and James Beard-level expertise',
           uniqueValue: 'We\'re creating the only fine dining experience that makes Boston\'s culinary heritage the star, not just a side note',
           target: 'The affluent professionals, business entertainers, and culinary enthusiasts who spend $150+ per person on special dining experiences',
           traction: 'Secured commitment from James Beard-nominated chef and received enthusiastic response from Boston\'s culinary community',
           ask: 'We\'re seeking $750,000 to establish Boston\'s first world-class New England cuisine destination and put our city on the global culinary map',
           finalPitch: '',
           practiceNotes: ''
         },
         executiveSummary: {
           businessName: 'Meridian',
          businessType: 'fine-dining',
          location: 'back-bay',
          fundingRequest: '$750,000',
          missionStatement: 'To create an elevated dining experience showcasing New England ingredients with global culinary techniques in Boston\'s sophisticated Back Bay.',
          keySuccessFactors: '• Prestigious Back Bay location near luxury hotels\n• James Beard-nominated executive chef\n• Sommelier-curated wine program\n• Private dining rooms for corporate events\n• Valet parking partnership',
          financialSummary: '• Projected Year 1 Revenue: $1,250,000\n• Target Gross Margin: 65%\n• Break-even Timeline: 24 months\n• Initial Investment: $750,000\n• Projected ROI: 16% by Year 4'
        },
        marketAnalysis: {
          industryOverview: 'Boston\'s fine dining market represents $180M annually, with Back Bay capturing 25% of high-end restaurant revenue.',
          targetMarket: 'Affluent professionals (40%), special occasion diners (35%), business entertainment (25%). Average check $120+.',
          marketSize: 'Back Bay fine dining: $45M annually. Targeting 2.8% market share.',
          competitiveAnalysis: 'Competitors: Grill 23, Deuxave, Sorellina. Differentiation through New England-focused tasting menus.',
          marketTrends: 'Growth in experiential dining, wine pairings, chef\'s table experiences, and corporate dining.',
          customerDemographics: 'Primary: High-income couples and business diners, ages 35-65, income $150K+.'
        },
        operationsPlan: {},
        managementTeam: {},
        serviceDescription: {},
        marketingStrategy: {}
      },
      financialData: {
        revenue: {
          foodSales: 1000000,
          beverageSales: 250000,
          merchandiseSales: 0,
          cateringSales: 0,
          otherRevenue: 0
        },
        cogs: {
          foodCogsPercent: 0.32,
          beverageCogsPercent: 0.25,
          merchandiseCogsPercent: 0.15,
          cateringCogsPercent: 0.32,
          otherCogsPercent: 0.12
        },
        operatingExpenses: {
          rent: 180000,
          utilities: 36000,
          insurance: 24000,
          marketing: 45000,
          legalAccounting: 18000,
          repairsMaintenance: 20000,
          supplies: 0,
          adminOffice: 0,
          otherOperatingExpenses: 0,
          salaryOwners: 120000,
          salaryFullTime: 350000,
          salaryPartTime: 180000,
          payrollTaxRate: 0.12
        },
        startupCosts: {
          leaseholdImprovements: 350000,
          kitchenEquipment: 200000,
          furnitureFixtures: 125000,
          initialInventory: 25000,
          preOpeningSalaries: 0,
          depositsLicenses: 35000,
          initialMarketing: 35000,
          contingency: 25000
        },
        fundingSources: {
          ownersEquity: 300000,
          investorFunds: 250000,
          bankLoans: 200000,
          otherFunding: 0
        }
      },
      vendors: [
        { id: 7, name: "Jeff Sullivan", company: "Boston Showcase Company", email: "jeff@bostonshowcase.com", category: "Equipment", priority: "medium" },
        { id: 11, name: "Ryan Pinkham", company: "Pink Projects", email: "ryan@pinkproj.com", category: "Construction", priority: "high" }
      ]
    },
    {
      id: 'demo_seaport_healthy_bowls',
      name: 'Seaport Healthy Bowls',
      createdAt: new Date(now.getTime() - 3 * hour),
      updatedAt: new Date(now.getTime() - 1 * hour),
      concept: 'Seaport Fast Casual',
      conceptId: 'concept_seaport_fast_casual',
      conceptDescription: 'Healthy fast-casual dining in Boston\'s growing Seaport district',
      businessPlan: {
         ideation: {
           businessConcept: 'A healthy fast-casual restaurant in Boston\'s Seaport that serves customizable bowls to busy tech professionals because there\'s a lack of quick, nutritious options in the area.',
           coreInspiration: 'Working long hours in the tech industry and struggling to find healthy, quick meal options that weren\'t expensive or time-consuming',
           targetAudience: 'Tech workers, young professionals, health-conscious millennials, and convention center visitors',
           initialProblem: 'Seaport professionals are stuck with expensive sit-down restaurants or unhealthy fast food, with limited healthy options for busy schedules',
           solutionIdea: 'Customizable healthy bowls with fresh ingredients, quick service, mobile ordering, and affordable pricing',
           differentiator: 'Focus on tech worker needs: fast WiFi, phone charging stations, healthy brain foods, and efficient mobile ordering',
           businessType: 'fast-casual',
           location: 'seaport',
           timeCommitment: 'full-time',
           investmentLevel: 'medium',
           successMeasure: 'Becoming the go-to lunch spot for Seaport professionals with 500+ daily customers and potential for expansion',
           personalMotivation: 'Solving my own problem while building a scalable business that promotes healthy eating habits',
           marketOpportunity: 'Bowl sales, corporate catering, meal subscriptions, branded merchandise, franchise expansion',
           resourcesNeeded: 'Restaurant management experience, healthy food knowledge, tech-savvy marketing, startup capital',
           riskAssessment: 'High Seaport rents, competition from chains, seasonal business fluctuations, need for tech integration'
         },
         elevatorPitch: {
           template: 'problem-solution',
           hook: 'What if I told you that Seaport tech workers spend an average of $18 and 45 minutes for lunch every day, but 78% say they want healthier, faster options?',
           problem: 'Boston\'s fastest-growing business district lacks affordable, healthy food options for busy professionals',
           solution: 'Harbor Bowl offers customizable, nutritious bowls with 5-minute service and mobile ordering designed specifically for the tech community',
           uniqueValue: 'We\'re the only restaurant combining healthy fast-casual with tech-worker amenities like charging stations and optimized WiFi',
           target: 'The 15,000+ tech workers, young professionals, and convention visitors in Boston\'s Seaport district',
           traction: 'Validated through surveys of 200+ Seaport workers and pre-orders from 3 major tech companies',
           ask: 'We\'re seeking $325,000 to capture Boston\'s fastest-growing market with a scalable, tech-forward healthy dining concept',
           finalPitch: '',
           practiceNotes: ''
         },
         executiveSummary: {
           businessName: 'Harbor Bowl',
          businessType: 'fast-casual',
          location: 'seaport',
          fundingRequest: '$325,000',
          missionStatement: 'To provide fresh, healthy, customizable bowl meals to Boston\'s busy Seaport professionals and residents.',
          keySuccessFactors: '• Modern Seaport location in growing business district\n• Health-focused menu with customizable options\n• Fast service model for busy professionals\n• Sustainable sourcing and eco-friendly packaging\n• Digital ordering and delivery integration',
          financialSummary: '• Projected Year 1 Revenue: $650,000\n• Target Gross Margin: 72%\n• Break-even Timeline: 14 months\n• Initial Investment: $325,000\n• Projected ROI: 22% by Year 3'
        },
        marketAnalysis: {
          industryOverview: 'Fast-casual dining is the fastest growing restaurant segment in Boston, with 12% annual growth.',
          targetMarket: 'Seaport professionals (60%), residents (25%), tourists/convention visitors (15%). Health-conscious, tech-savvy demographics.',
          marketSize: 'Seaport dining market: $85M annually. Fast-casual segment: $25M. Targeting 2.6% market share.',
          competitiveAnalysis: 'Competitors: Sweetgreen, CAVA, Dig Inn. Differentiation through local Boston ingredients and harbor-themed atmosphere.',
          marketTrends: 'Rising demand for healthy fast-food, customization, mobile ordering, and sustainable practices.',
          customerDemographics: 'Primary: Working professionals, ages 25-45, income $85K+. Secondary: Health-conscious millennials and Gen Z.'
        },
        operationsPlan: {},
        managementTeam: {},
        serviceDescription: {},
        marketingStrategy: {}
      },
      financialData: {
        revenue: {
          foodSales: 520000,
          beverageSales: 85000,
          merchandiseSales: 15000,
          cateringSales: 30000,
          otherRevenue: 0
        },
        cogs: {
          foodCogsPercent: 0.26,
          beverageCogsPercent: 0.20,
          merchandiseCogsPercent: 0.40,
          cateringCogsPercent: 0.30,
          otherCogsPercent: 0.12
        },
        operatingExpenses: {
          rent: 96000,
          utilities: 18000,
          insurance: 14000,
          marketing: 35000,
          legalAccounting: 8000,
          repairsMaintenance: 10000,
          supplies: 0,
          adminOffice: 0,
          otherOperatingExpenses: 0,
          salaryOwners: 65000,
          salaryFullTime: 120000,
          salaryPartTime: 85000,
          payrollTaxRate: 0.12
        },
        startupCosts: {
          leaseholdImprovements: 120000,
          kitchenEquipment: 95000,
          furnitureFixtures: 35000,
          initialInventory: 12000,
          preOpeningSalaries: 0,
          depositsLicenses: 25000,
          initialMarketing: 25000,
          contingency: 13000
        },
        fundingSources: {
          ownersEquity: 150000,
          investorFunds: 100000,
          bankLoans: 75000,
          otherFunding: 0
        }
      },
      vendors: [
        { id: 3, name: "Craig Newton", company: "Supplies on the Fly", email: "cnewton@suppliesonthefly.com", category: "Restaurant Supplies", priority: "medium" },
        { id: 8, name: "Amie Raskin", company: "Intelligentsia Coffee Inc", email: "araskin@intelligentsiacoffee.com", category: "Beverage Supplier", priority: "medium" }
      ]
    },
    {
      id: 'demo_the_beacon',
      name: 'The Beacon - Sample',
      createdAt: new Date(now.getTime() - 2 * hour),
      updatedAt: new Date(now.getTime() - 0.5 * hour),
      concept: 'Modern American Bistro',
      conceptId: 'concept_the_beacon',
      conceptDescription: 'Modern American bistro serving elevated comfort food with local ingredients in Back Bay',
      businessPlan: {
        ideation: {
          businessConcept: 'A modern American bistro in Boston\'s Back Bay that serves elevated comfort food using locally-sourced ingredients, creating a warm neighborhood gathering place for the community.',
          coreInspiration: 'Creating a neighborhood gathering place that serves elevated comfort food using locally-sourced ingredients. The Beacon will be a beacon of community, great food, and warm hospitality.',
          targetAudience: 'Young professionals (25-40), food enthusiasts interested in local ingredients, neighborhood residents looking for a "third place", date night and special occasion diners, business professionals for lunch meetings',
          initialProblem: 'Back Bay lacks a modern American bistro that focuses on elevated comfort food with local sourcing and craft cocktails in a warm, community-focused atmosphere',
          solutionIdea: 'The Beacon will serve modern American comfort food made with locally-sourced ingredients, feature a craft cocktail program with local spirits, offer rotating seasonal menus, and create community-focused events and programming',
          differentiator: 'Only restaurant in Back Bay focusing exclusively on modern American comfort food with local sourcing, rotating seasonal menu highlighting New England ingredients, craft cocktail program featuring local spirits, and community-focused events',
          businessType: 'restaurant',
          location: 'back-bay',
          timeCommitment: 'intensive',
          investmentLevel: 'high',
          successMeasure: 'Building a beloved neighborhood institution that serves exceptional food, creates community connections, and provides financial stability with $1.2M annual revenue by year 2',
          personalMotivation: 'Combining my passion for local food, community building, and hospitality to create a restaurant that becomes a cornerstone of the Back Bay neighborhood',
          marketOpportunity: 'Back Bay has high foot traffic, affluent demographics, strong tourism, and demand for quality dining experiences with local focus',
          resourcesNeeded: 'Prime Back Bay location, experienced kitchen and service staff, relationships with local farms and purveyors, craft cocktail expertise, community engagement skills, $425K startup capital',
          riskAssessment: 'High competition in Back Bay, need for strong differentiation, importance of location and visibility, need for experienced team, managing food costs with local sourcing'
        },
        elevatorPitch: {
          template: 'problem-solution',
          hook: 'Back Bay has 280 restaurants, but none that serve modern American comfort food with a true local focus and community spirit.',
          problem: 'Food lovers in Back Bay want quality dining experiences with local ingredients, but most restaurants serve generic fare without community connection.',
          solution: 'The Beacon is a modern American bistro serving elevated comfort food made with locally-sourced ingredients, craft cocktails, and a warm community atmosphere.',
          uniqueValue: 'We\'re the only Back Bay restaurant combining modern American comfort food, local sourcing, craft cocktails, and genuine community engagement in one welcoming space.',
          target: 'Young professionals, food enthusiasts, neighborhood residents, and anyone seeking quality dining with local focus and community connection.',
          traction: 'Back Bay market: $180M annually, 280 restaurants, 3.2% growth. Target: 0.5-0.8% market share ($1.1M-$1.8M revenue).',
          ask: 'Seeking $425K to open The Beacon in Back Bay, targeting $1.2M Year 1 revenue, 24% net margin, Month 8 break-even.',
          finalPitch: 'The Beacon will be Back Bay\'s modern American bistro - serving elevated comfort food with local ingredients, craft cocktails, and community spirit. We\'re targeting $1.2M Year 1 revenue with 24% margins. Seeking $425K to open in prime Back Bay location.',
          practiceNotes: 'Emphasize local sourcing, community focus, and strong financial projections. Highlight Back Bay market opportunity and differentiation.'
        },
        executiveSummary: {
          businessName: 'The Beacon',
          businessType: 'Modern American Bistro',
          location: 'Back Bay, Boston, MA',
          fundingRequest: '$425,000',
          missionStatement: 'The Beacon is a modern American bistro dedicated to serving elevated comfort food made with locally-sourced ingredients. We create a warm, welcoming atmosphere where community gathers, relationships are built, and memorable dining experiences happen.',
          keySuccessFactors: 'Exceptional food quality using local ingredients, warm welcoming service and atmosphere, strong community presence and engagement, strategic Back Bay location with high foot traffic, efficient operations and cost management',
          financialSummary: 'Startup Investment: $425,000. Annual Revenue Projection (Year 1): $1,200,000. Break-even: Month 8. 3-Year Revenue Target: $1,800,000. Net Margin: 24%. ROI Year 1: 68%.'
        },
        marketAnalysis: {
          industryOverview: 'Boston\'s Back Bay restaurant market represents a $180M annual market with 280 restaurants. The area has shown consistent 3.2% growth, driven by strong local economy, tourism (21.2M visitors annually), and high disposable income ($125,000 median household income).',
          targetMarket: 'Primary: Affluent professionals and tourists (ages 28-45, income $85K+). Secondary: Neighborhood residents, food enthusiasts, business diners seeking quality experiences with local focus.',
          marketSize: 'Back Bay dining market: Approximately $224M annually. Targeting 0.5-0.8% market share ($1.1M - $1.8M annual revenue).',
          competitiveAnalysis: 'Key competitors: 280 restaurants in area. Competition level: Very High. Differentiation through modern American comfort food, local sourcing, craft cocktails, and community focus.',
          marketTrends: 'Growing demand for local sourcing, craft cocktails, farm-to-table, unique positioning in competitive market, community engagement, seasonal menus.',
          customerDemographics: 'Primary: Affluent professionals and tourists, ages 28-45, income $85,000+. Secondary: Neighborhood residents, food enthusiasts, business professionals.'
        },
        operationsPlan: {
          location: 'Back Bay, Boston, MA - Prime location with high foot traffic and visibility',
          facilityRequirements: '2,400 sq ft space with kitchen, dining room (45 seats), bar (12 seats), patio (8 seats), private dining room, storage, office space',
          hoursOfOperation: 'Monday-Thursday: 11:30 AM - 10:00 PM, Friday-Saturday: 11:30 AM - 11:00 PM, Sunday: 11:00 AM - 9:00 PM',
          staffingPlan: 'Owner/Chef (full-time), General Manager (1 FTE), Sous Chef (1 FTE), Line Cooks (2 FTE), Servers (4-6 part-time), Bartenders (2 part-time), Host/Busser (2 part-time)',
          supplierRelationships: 'Primary food distributor: Local farms and specialty purveyors. Beverage distributor: Local craft spirits and wine. Equipment maintenance: Service contracts with key vendors.',
          qualityControl: 'Daily line checks and food safety protocols, weekly menu reviews and cost analysis, monthly staff training sessions, quarterly vendor performance reviews'
        },
        managementTeam: {
          keyPersonnel: 'Chef/Owner: 15+ years culinary experience, previous restaurant management. General Manager: Restaurant operations and service management (to be hired). Sous Chef: Kitchen operations and menu execution (to be hired).',
          organizationalStructure: 'Owner/Chef oversees all operations. General Manager handles front-of-house, service, and day-to-day operations. Sous Chef manages kitchen operations and staff.',
          advisoryBoard: 'Local restaurant consultant, food industry advisor, financial advisor'
        },
        serviceDescription: {
          menuConcept: 'Modern American comfort food with seasonal rotations. Focus on local ingredients, elevated classics, and approachable fine dining. Rotating seasonal menu highlighting New England ingredients.',
          serviceType: 'Full-service dining with bar service, takeout options, catering for events, private dining room for special occasions',
          uniqueFeatures: 'Craft cocktail program with local spirits, seasonal menu changes, community events, private dining room, takeout and delivery options'
        },
        marketingStrategy: {
          marketingMix: 'Product: Modern American comfort food with local ingredients, craft cocktails, seasonal menus. Price: Mid-range ($35-55 per person average check). Place: Back Bay location with high foot traffic and visibility. Promotion: Social media, local partnerships, community events, email marketing.',
          salesStrategy: 'Target audience: Affluent professionals and tourists. Peak hours: Lunch (12-2pm), Dinner (7-9pm), Weekend brunch. Sales channels: In-house dining, takeout, catering, private events.',
          customerAcquisition: 'Primary: Social media (Instagram, Facebook), Google Maps, local food blogs, neighborhood partnerships. Secondary: Email marketing, community events, referral program.',
          brandingStrategy: 'Position as modern American bistro with local focus. Target affluent professionals and food enthusiasts with warm, community-focused messaging.',
          digitalMarketing: 'Website: SEO for Back Bay restaurants. Social media: Instagram, Facebook for food photography and community engagement. Online advertising: Google Ads for affluent professionals and tourists.'
        }
      },
      financialData: {
        revenue: {
          foodSales: 850000,
          beverageSales: 280000,
          merchandiseSales: 12000,
          cateringSales: 58000,
          otherRevenue: 0
        },
        cogs: {
          foodCogsPercent: 0.28,
          beverageCogsPercent: 0.22,
          merchandiseCogsPercent: 0.40,
          cateringCogsPercent: 0.30,
          otherCogsPercent: 0.12
        },
        operatingExpenses: {
          rent: 120000,
          utilities: 18000,
          insurance: 15000,
          marketing: 42000,
          legalAccounting: 12000,
          repairsMaintenance: 15000,
          supplies: 8000,
          adminOffice: 5000,
          otherOperatingExpenses: 3000,
          salaryOwners: 75000,
          salaryFullTime: 180000,
          salaryPartTime: 95000,
          payrollTaxRate: 0.12
        },
        restaurantOperations: {
          squareFootage: 2400,
          seats: 65,
          hoursOfOperation: {
            monday: { closed: false, open: '11:30', close: '22:00' },
            tuesday: { closed: false, open: '11:30', close: '22:00' },
            wednesday: { closed: false, open: '11:30', close: '22:00' },
            thursday: { closed: false, open: '11:30', close: '22:00' },
            friday: { closed: false, open: '11:30', close: '23:00' },
            saturday: { closed: false, open: '11:30', close: '23:00' },
            sunday: { closed: false, open: '11:00', close: '21:00' }
          }
        },
        startupCosts: {
          leaseholdImprovements: 145000,
          kitchenEquipment: 110000,
          furnitureFixtures: 42000,
          initialInventory: 15000,
          preOpeningSalaries: 18000,
          depositsLicenses: 28000,
          initialMarketing: 30000,
          contingency: 19000
        },
        fundingSources: {
          personalSavings: 150000,
          sbaLoans: 200000,
          familyLoans: 50000,
          equipmentFinancing: 25000,
          bankLoans: 0,
          lineOfCredit: 0,
          businessCreditCards: 0,
          crowdfunding: 0,
          angelInvestors: 0,
          ventureCapital: 0,
          grants: 0,
          privateInvestors: 0,
          businessPartners: 0,
          supplierCredit: 0,
          otherSources: 0
        },
        restaurantType: {
          type: 'new'
        }
      },
      vendors: []
    }
  ];
};

const getCurrentDraft = (state) => {
  if (!state.currentDraftId || state.drafts.length === 0) return null;
  return state.drafts.find(draft => draft.id === state.currentDraftId) || null;
};

const updateCurrentDraftData = (state) => {
  const currentDraft = getCurrentDraft(state);
  if (currentDraft) {
    return {
      ...state,
      businessPlan: currentDraft.businessPlan || state.businessPlan,
      financialData: currentDraft.financialData && typeof currentDraft.financialData === 'object' ? currentDraft.financialData : (state.financialData || {}),
      vendors: Array.isArray(currentDraft.vendors) ? currentDraft.vendors : [],
      openingPlanProgress: currentDraft.openingPlanProgress || { completedTaskIds: [] },
      documentVersions: Array.isArray(currentDraft.documentVersions) ? currentDraft.documentVersions : [],
      sops: Array.isArray(currentDraft.sops) ? currentDraft.sops : [],
      ledgerEntries: Array.isArray(currentDraft.ledgerEntries) ? currentDraft.ledgerEntries : [],
      equipmentData: currentDraft.equipmentData || null
    };
  }
  return state;
};

// App Reducer
export const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
      
    case ActionTypes.SET_AUTHENTICATED:
      return { ...state, isAuthenticated: action.payload };
      
    case ActionTypes.SET_USER_ID:
      return { ...state, userId: action.payload };
      
    case ActionTypes.SET_USER:
      return { ...state, user: action.payload };
    
    case ActionTypes.UPDATE_USER_PROFILE:
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : state.user,
      };
      
    case ActionTypes.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload };
      
    case ActionTypes.SHOW_MESSAGE:
      return {
        ...state,
        showMessage: true,
        message: action.payload
      };
      
    case ActionTypes.HIDE_MESSAGE:
      return {
        ...state,
        showMessage: false,
        message: { type: 'info', title: '', text: '' }
      };
      
    case ActionTypes.SHOW_WELCOME_MESSAGE:
      return {
        ...state,
        showWelcomeMessage: true
      };
      
    case ActionTypes.HIDE_WELCOME_MESSAGE:
      return {
        ...state,
        showWelcomeMessage: false
      };
      
    case ActionTypes.UPDATE_BUSINESS_PLAN: {
      const updatedBusinessPlan = {
        ...state.businessPlan,
        [action.payload.section]: {
          ...state.businessPlan[action.payload.section],
          ...action.payload.data
        }
      };
      
      // Update current draft
      const updatedDrafts = state.drafts.map(draft => 
        draft.id === state.currentDraftId 
          ? { ...draft, businessPlan: updatedBusinessPlan, updatedAt: new Date() }
          : draft
      );
      
      return {
        ...state,
        businessPlan: updatedBusinessPlan,
        drafts: updatedDrafts
      };
    }
    
    case ActionTypes.UPDATE_FINANCIAL_DATA: {
      const current = state.financialData || {};
      const updatedFinancialData = {
        ...current,
        [action.payload.section]: {
          ...(current[action.payload.section] || {}),
          ...action.payload.data
        }
      };

      // When restaurant location changes, sync to executive summary and ideation
      let updatedBusinessPlan = state.businessPlan;
      if (action.payload.section === 'restaurantDetails' && action.payload.data?.location != null) {
        const newLocation = action.payload.data.location;
        updatedBusinessPlan = {
          ...state.businessPlan,
          executiveSummary: {
            ...state.businessPlan.executiveSummary,
            location: newLocation
          }
        };
      }
      
      // Update current draft
      const updatedDrafts = state.drafts.map(draft => {
        if (draft.id !== state.currentDraftId) return draft;
        const draftUpdates = { financialData: updatedFinancialData, updatedAt: new Date() };
        if (updatedBusinessPlan !== state.businessPlan) {
          draftUpdates.businessPlan = updatedBusinessPlan;
        }
        return { ...draft, ...draftUpdates };
      });
      
      return {
        ...state,
        financialData: updatedFinancialData,
        businessPlan: updatedBusinessPlan,
        drafts: updatedDrafts
      };
    }
    
    case ActionTypes.SET_VENDORS: {
      // Update current draft
      const updatedDrafts = state.drafts.map(draft => 
        draft.id === state.currentDraftId 
          ? { ...draft, vendors: action.payload, updatedAt: new Date() }
          : draft
      );
      
      return {
        ...state,
        vendors: action.payload,
        drafts: updatedDrafts
      };
    }
    
    case ActionTypes.ADD_VENDOR: {
      const currentVendors = Array.isArray(state.vendors) ? state.vendors : [];
      const updatedVendors = [...currentVendors, action.payload];
      
      // Update current draft
      const updatedDrafts = state.drafts.map(draft => 
        draft.id === state.currentDraftId 
          ? { ...draft, vendors: updatedVendors, updatedAt: new Date() }
          : draft
      );
      
      return {
        ...state,
        vendors: updatedVendors,
        drafts: updatedDrafts
      };
    }
    
    case ActionTypes.UPDATE_VENDOR: {
      const updatedVendor = action.payload;
      const updatedVendors = state.vendors.map(vendor => 
        vendor.id === updatedVendor.id ? updatedVendor : vendor
      );
      
      // Update current draft
      const updatedDrafts = state.drafts.map(draft => 
        draft.id === state.currentDraftId 
          ? { ...draft, vendors: updatedVendors, updatedAt: new Date() }
          : draft
      );
      
      return {
        ...state,
        vendors: updatedVendors,
        drafts: updatedDrafts
      };
    }
    
    case ActionTypes.REMOVE_VENDOR: {
      const currentVendors = Array.isArray(state.vendors) ? state.vendors : [];
      const updatedVendors = currentVendors.filter(vendor => vendor.id !== action.payload);
      
      // Update current draft
      const updatedDrafts = state.drafts.map(draft => 
        draft.id === state.currentDraftId 
          ? { ...draft, vendors: updatedVendors, updatedAt: new Date() }
          : draft
      );
      
      return {
        ...state,
        vendors: updatedVendors,
        drafts: updatedDrafts
      };
    }
    
    case ActionTypes.UPDATE_CERTIFICATIONS: {
      // Update current draft
      const updatedDrafts = state.drafts.map(draft => 
        draft.id === state.currentDraftId 
          ? { ...draft, certifications: action.payload, updatedAt: new Date() }
          : draft
      );
      
      return {
        ...state,
        certifications: action.payload,
        drafts: updatedDrafts
      };
    }
    
    // Draft management actions
    case ActionTypes.SET_DRAFTS: {
      const drafts = Array.isArray(action.payload) ? action.payload : [];
      const currentDraftId = state.currentDraftId != null && drafts.some(d => d.id === state.currentDraftId)
        ? state.currentDraftId
        : (drafts.length > 0 ? drafts[0].id : null);
      const newState = {
        ...state,
        drafts,
        currentDraftId
      };
      return updateCurrentDraftData(newState);
    }
    
    case ActionTypes.SET_CURRENT_DRAFT_ID: {
      const newState = {
        ...state,
        currentDraftId: action.payload,
        equipmentData: null // Will be hydrated by updateCurrentDraftData
      };
      return updateCurrentDraftData(newState);
    }
    
    case ActionTypes.CREATE_DRAFT: {
      const newDraft = createNewDraft(
        action.payload.name,
        action.payload.baseDraft,
        action.payload.projectIntent,
        action.payload.enabledFeatures,
        action.payload.initialLocation
      );
      return {
        ...state,
        drafts: [...state.drafts, newDraft],
        currentDraftId: newDraft.id,
        businessPlan: newDraft.businessPlan,
        financialData: newDraft.financialData,
        vendors: newDraft.vendors,
        openingPlanProgress: newDraft.openingPlanProgress || { completedTaskIds: [] },
        documentVersions: newDraft.documentVersions || [],
        sops: newDraft.sops || [],
        equipmentData: newDraft.equipmentData || null,
        ledgerEntries: newDraft.ledgerEntries || []
      };
    }

    case ActionTypes.CREATE_DRAFT_FROM_IMPORT: {
      const newDraft = action.payload.draft;
      return {
        ...state,
        drafts: [...state.drafts, newDraft],
        currentDraftId: newDraft.id,
        businessPlan: newDraft.businessPlan,
        financialData: newDraft.financialData,
        vendors: newDraft.vendors,
        openingPlanProgress: newDraft.openingPlanProgress || { completedTaskIds: [] },
        documentVersions: newDraft.documentVersions || [],
        sops: newDraft.sops || [],
        equipmentData: newDraft.equipmentData || null,
        ledgerEntries: newDraft.ledgerEntries || []
      };
    }
    
    case ActionTypes.UPDATE_DRAFT: {
      const updatedDrafts = state.drafts.map(draft => 
        draft.id === action.payload.id 
          ? { ...draft, ...action.payload.updates, updatedAt: new Date() }
          : draft
      );
      
      const newState = {
        ...state,
        drafts: updatedDrafts
      };
      return updateCurrentDraftData(newState);
    }
    
    case ActionTypes.DELETE_DRAFT: {
      const remainingDrafts = state.drafts.filter(draft => draft.id !== action.payload);
      const newCurrentDraftId = state.currentDraftId === action.payload 
        ? (remainingDrafts.length > 0 ? remainingDrafts[0].id : null)
        : state.currentDraftId;
      
      const newState = {
        ...state,
        drafts: remainingDrafts,
        currentDraftId: newCurrentDraftId
      };
      return updateCurrentDraftData(newState);
    }
    
    case ActionTypes.DUPLICATE_DRAFT: {
      const originalDraft = state.drafts.find(draft => draft.id === action.payload.originalId);
      if (!originalDraft) return state;
      
      const duplicatedDraft = createNewDraft(action.payload.name, originalDraft);
      return {
        ...state,
        drafts: [...state.drafts, duplicatedDraft]
      };
    }
    
    case ActionTypes.SET_DRAFT_COMPARISON:
      return {
        ...state,
        draftComparison: action.payload
      };
    
    case ActionTypes.UPDATE_PROJECT_TIMELINE:
      return {
        ...state,
        projectTimeline: action.payload
      };

    case ActionTypes.UPDATE_OPENING_PLAN_PROGRESS: {
      const completedTaskIds = Array.isArray(action.payload) ? action.payload : (action.payload?.completedTaskIds || []);
      const openingPlanProgress = { completedTaskIds };
      const updatedDrafts = state.drafts.map(draft =>
        draft.id === state.currentDraftId ? { ...draft, openingPlanProgress, updatedAt: new Date() } : draft
      );
      return {
        ...state,
        openingPlanProgress,
        drafts: updatedDrafts
      };
    }
    
    case ActionTypes.SET_SAVE_STATUS:
      return {
        ...state,
        saveStatus: action.payload
      };
    
    case ActionTypes.SET_LAST_SAVED_AT:
      return {
        ...state,
        lastSavedAt: action.payload
      };

    case ActionTypes.UPDATE_DOCUMENT_VERSIONS: {
      const documentVersions = action.payload;
      const updatedDrafts = state.drafts.map(draft =>
        draft.id === state.currentDraftId ? { ...draft, documentVersions, updatedAt: new Date() } : draft
      );
      return { ...state, documentVersions, drafts: updatedDrafts };
    }

    case ActionTypes.UPDATE_SOPS: {
      const sops = action.payload;
      const updatedDrafts = state.drafts.map(draft =>
        draft.id === state.currentDraftId ? { ...draft, sops, updatedAt: new Date() } : draft
      );
      return { ...state, sops, drafts: updatedDrafts };
    }

    case ActionTypes.UPDATE_LEDGER_ENTRIES: {
      const ledgerEntries = action.payload;
      const updatedDrafts = state.drafts.map(draft =>
        draft.id === state.currentDraftId ? { ...draft, ledgerEntries, updatedAt: new Date() } : draft
      );
      return { ...state, ledgerEntries, drafts: updatedDrafts };
    }

    case ActionTypes.MARK_NOTIFICATIONS_READ:
      return {
        ...state,
        notificationReadIds: Array.isArray(action.payload)
          ? [...new Set([...state.notificationReadIds, ...action.payload])]
          : state.notificationReadIds
      };
    
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const assessmentImportInProgress = useRef(false);

  // Action creators
  const actions = {
    setLoading: (isLoading) => {
      dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });
    },
    
    setAuthenticated: (isAuthenticated) => {
      dispatch({ type: ActionTypes.SET_AUTHENTICATED, payload: isAuthenticated });
    },
    
    setUserId: (userId) => {
      dispatch({ type: ActionTypes.SET_USER_ID, payload: userId });
    },
    
    setUser: (user) => {
      dispatch({ type: ActionTypes.SET_USER, payload: user });
    },
    
    updateUserProfile: (profile) => {
      dispatch({ type: ActionTypes.UPDATE_USER_PROFILE, payload: profile });
    },
    
    setActiveTab: (tab) => {
      dispatch({ type: ActionTypes.SET_ACTIVE_TAB, payload: tab });
    },
    
    showMessage: (title, text, type = 'info') => {
      dispatch({ type: ActionTypes.SHOW_MESSAGE, payload: { title, text, type } });
    },
    
    hideMessage: () => {
      dispatch({ type: ActionTypes.HIDE_MESSAGE });
    },
    
    showWelcomeMessage: () => {
      dispatch({ type: ActionTypes.SHOW_WELCOME_MESSAGE });
    },
    
    hideWelcomeMessage: () => {
      dispatch({ type: ActionTypes.HIDE_WELCOME_MESSAGE });
    },
    
    updateBusinessPlan: (section, data) => {
      dispatch({ type: ActionTypes.UPDATE_BUSINESS_PLAN, payload: { section, data } });
    },
    
    updateFinancialData: (section, data) => {
      dispatch({ type: ActionTypes.UPDATE_FINANCIAL_DATA, payload: { section, data } });
    },
    
    setVendors: (vendors) => {
      dispatch({ type: ActionTypes.SET_VENDORS, payload: vendors });
    },
    
    addVendor: (vendor) => {
      dispatch({ type: ActionTypes.ADD_VENDOR, payload: vendor });
    },
    
    updateVendor: (vendor) => {
      dispatch({ type: ActionTypes.UPDATE_VENDOR, payload: vendor });
    },
    
    removeVendor: (id) => {
      dispatch({ type: ActionTypes.REMOVE_VENDOR, payload: id });
    },
    
    updateCertifications: (certifications) => {
      dispatch({ type: ActionTypes.UPDATE_CERTIFICATIONS, payload: certifications });
    },
    
    // Draft management actions
    setDrafts: (drafts) => {
      dispatch({ type: ActionTypes.SET_DRAFTS, payload: drafts });
    },
    
    setCurrentDraftId: (draftId) => {
      dispatch({ type: ActionTypes.SET_CURRENT_DRAFT_ID, payload: draftId });
    },
    
    createDraft: (name, baseDraft = null, projectIntent = null, enabledFeatures = null, initialLocation = null) => {
      dispatch({ type: ActionTypes.CREATE_DRAFT, payload: { name, baseDraft, projectIntent, enabledFeatures, initialLocation } });
      const newDraft = createNewDraft(name, baseDraft, projectIntent, enabledFeatures, initialLocation);
      return newDraft;
    },
    createDraftFromImport: (name, overrides = {}) => {
      const draft = createDraftFromPendingImport(name, overrides);
      dispatch({ type: ActionTypes.CREATE_DRAFT_FROM_IMPORT, payload: { draft } });
      return draft;
    },
    
    updateDraft: (id, updates) => {
      dispatch({ type: ActionTypes.UPDATE_DRAFT, payload: { id, updates } });
    },
    
    deleteDraft: (draftId) => {
      dispatch({ type: ActionTypes.DELETE_DRAFT, payload: draftId });
    },
    
    duplicateDraft: (originalId, name) => {
      dispatch({ type: ActionTypes.DUPLICATE_DRAFT, payload: { originalId, name } });
    },
    
    setDraftComparison: (comparison) => {
      dispatch({ type: ActionTypes.SET_DRAFT_COMPARISON, payload: comparison });
    },
    
    updateProjectTimeline: (timeline) => {
      dispatch({ type: ActionTypes.UPDATE_PROJECT_TIMELINE, payload: timeline });
    },

    updateOpeningPlanProgress: (completedTaskIds) => {
      dispatch({ type: ActionTypes.UPDATE_OPENING_PLAN_PROGRESS, payload: Array.isArray(completedTaskIds) ? completedTaskIds : (completedTaskIds?.completedTaskIds || []) });
    },
    updateDocumentVersions: (documentVersions) => {
      dispatch({ type: ActionTypes.UPDATE_DOCUMENT_VERSIONS, payload: documentVersions });
    },
    updateSops: (sops) => {
      dispatch({ type: ActionTypes.UPDATE_SOPS, payload: sops });
    },
    updateLedgerEntries: (ledgerEntries) => {
      dispatch({ type: ActionTypes.UPDATE_LEDGER_ENTRIES, payload: ledgerEntries });
    },
    updateEquipmentData: (equipmentData) => {
      dispatch({ type: ActionTypes.UPDATE_EQUIPMENT_DATA, payload: equipmentData });
    },
    markNotificationsRead: (ids) => {
      dispatch({ type: ActionTypes.MARK_NOTIFICATIONS_READ, payload: ids });
    },
    
    // Save data to Firebase (with optional silent mode for auto-save)
    saveData: async (silent = false) => {
      if (!state.userId) {
        if (!silent) {
          actions.showMessage('Error', 'Please sign in to save your data.', 'error');
        }
        return false;
      }
      
      if (!state.currentDraftId) {
        if (!silent) {
          actions.showMessage('Error', 'No draft selected. Please create or select a draft first.', 'error');
        }
        return false;
      }
      
      try {
        if (!silent) {
          actions.setLoading(true);
        }
        dispatch({ type: ActionTypes.SET_SAVE_STATUS, payload: 'saving' });
        
        const appId = getAppId();
        
        // Update current draft with latest data
        const currentDraft = getCurrentDraft(state);
        if (currentDraft) {
          const updatedDraft = {
            ...currentDraft,
            name: currentDraft.name || 'Untitled Draft', // Ensure name is always included
            businessPlan: state.businessPlan,
            financialData: state.financialData || {},
            vendors: Array.isArray(state.vendors) ? state.vendors : [],
            openingPlanProgress: state.openingPlanProgress || { completedTaskIds: [] },
            documentVersions: Array.isArray(state.documentVersions) ? state.documentVersions : [],
            sops: Array.isArray(state.sops) ? state.sops : [],
            ledgerEntries: Array.isArray(state.ledgerEntries) ? state.ledgerEntries : [],
            updatedAt: new Date()
          };
          
          // Save individual draft
          await dbService.saveDraft(state.userId, appId, updatedDraft);
          
          // Save drafts metadata
          const updatedDrafts = state.drafts.map(draft => 
            draft.id === state.currentDraftId ? updatedDraft : draft
          );
          await dbService.saveDraftsMetadata(state.userId, appId, updatedDrafts);
          
          dispatch({ type: ActionTypes.SET_SAVE_STATUS, payload: 'saved' });
          dispatch({ type: ActionTypes.SET_LAST_SAVED_AT, payload: new Date() });
          
          // Briefly show saved, then hide indicator to reduce distraction
          setTimeout(() => {
            dispatch({ type: ActionTypes.SET_SAVE_STATUS, payload: 'idle' });
          }, 1500);
          
          if (!silent) {
            actions.showMessage('Success', `Draft "${currentDraft.name}" saved successfully!`, 'success');
          }
          return true;
        } else {
          if (!silent) {
            actions.showMessage('Error', 'No current draft found. Please create or select a draft first.', 'error');
          }
          dispatch({ type: ActionTypes.SET_SAVE_STATUS, payload: 'error' });
          return false;
        }
      } catch (error) {
        dispatch({ type: ActionTypes.SET_SAVE_STATUS, payload: 'error' });
        if (!silent) {
          actions.showMessage('Error', `Failed to save draft: ${error.message || 'Unknown error'}. Please try again.`, 'error');
        }
        return false;
      } finally {
        if (!silent) {
          actions.setLoading(false);
        }
      }
    },

    // User management functions
    getAllUsers: async () => {
      return await getAllUsers();
    },

    deleteUserAccount: async (userId) => {
      return await deleteUserAccount(userId);
    },

    signInWithEmail: async (email, password) => {
      try {
        actions.setLoading(true);
        const result = await authService.signInWithEmailAndPassword(email, password);
        return result;
      } catch (error) {
        // console.error('Error signing in with email:', error);
        throw error;
      } finally {
        actions.setLoading(false);
      }
    },

    signInWithGoogle: async () => {
      try {
        actions.setLoading(true);
        const result = await authService.signInWithGoogle();
        return result;
      } catch (error) {
        // console.error('Error signing in with Google:', error);
        throw error;
      } finally {
        actions.setLoading(false);
      }
    },


    signOut: async () => {
      try {
        actions.setLoading(true);
        await authService.signOut();
        // Reset state
        dispatch({ type: ActionTypes.SET_AUTHENTICATED, payload: false });
        dispatch({ type: ActionTypes.SET_USER_ID, payload: null });
        dispatch({ type: ActionTypes.SET_USER, payload: null });
        dispatch({ type: ActionTypes.SET_DRAFTS, payload: [] });
        dispatch({ type: ActionTypes.SET_CURRENT_DRAFT_ID, payload: null });
        return true;
      } catch (error) {
        // console.error('Error signing out:', error);
        throw error;
      } finally {
        actions.setLoading(false);
      }
    }
  };

  // Initialize authentication and drafts
  useEffect(() => {
    let unsubscribe = null;
    let profileUnsubscribe = null;
    
    const initAuth = async () => {
      try {
        const initialToken = getInitialAuthToken();
        
        if (initialToken) {
          await authService.signInWithCustomToken(initialToken);
        }
        // No automatic anonymous sign-in - user must authenticate
      } catch (error) {
        // console.error('Authentication error:', error);
        actions.setLoading(false);
      }
    };
    
    // Set up auth state listener
    unsubscribe = authService.onAuthStateChanged(async (user) => {
      if (user) {
        const appId = getAppId();
        
        // Ensure user profile exists (for Stripe webhooks) and load subscription
        try {
          await dbService.ensureUserProfile(user.uid, appId, {
            email: user.email,
            displayName: user.displayName,
          });
          const profile = await dbService.getUserProfile(user.uid, appId);
          const userWithProfile = {
            ...user,
            ...(profile || {}),
            subscription: profile?.subscription || {
              status: 'active',
              plan: 'free',
              currentPeriodEnd: null,
              cancelAtPeriodEnd: false,
            },
          };
          actions.setUser(userWithProfile);
        } catch (profileError) {
          actions.setUser(user);
        }
        
        actions.setUserId(user.uid);
        actions.setAuthenticated(true);
        
                 // Load drafts data
         try {
           const draftsData = await dbService.getDrafts(user.uid, appId);
           
           if (draftsData && draftsData.length > 0) {
             // console.log('Loading existing drafts:', draftsData.length);
             actions.setDrafts(draftsData);
             // Set the most recent updated draft as current
             const mostRecent = draftsData.reduce((latest, draft) => 
               new Date(draft.updatedAt) > new Date(latest.updatedAt) ? draft : latest
             );
             // console.log('Setting current draft to:', mostRecent.id);
             actions.setCurrentDraftId(mostRecent.id);
           } else {
             // Check if we should load sample data for new users
             const shouldLoadSamples = 
               window.location.hostname === 'localhost' || 
               window.location.search.includes('demo=true') ||
               window.location.search.includes('samples=true') ||
               process.env.REACT_APP_LOAD_SAMPLES === 'true';
             
             if (shouldLoadSamples) {
               // Load sample drafts for new users
               // console.log('Loading sample drafts for new user');
               const sampleDrafts = createSampleDrafts();
               actions.setDrafts(sampleDrafts);
               actions.setCurrentDraftId(sampleDrafts[0].id);
               
               // Show welcome message for new users
               setTimeout(() => {
                 actions.showMessage(
                   'Welcome!', 
                   'We\'ve loaded 3 sample Boston restaurant concepts for you to explore. Try switching between drafts, comparing them, or creating your own!', 
                   'info'
                 );
               }, 1000);
             } else {
               // Production: leave drafts empty so user sees project setup (opening new / buying / helping existing)
               // First draft is created when they complete ProjectSetupModal in Dashboard
             }
           }
         } catch (error) {
           // console.error('Error loading drafts:', error);
           // On error leave drafts empty; user can create via project setup
         }
        
        // Subscribe to user profile for real-time subscription updates (e.g. after Stripe checkout)
        profileUnsubscribe = dbService.subscribeToUserProfile(user.uid, appId, (profile) => {
          if (profile) {
            actions.updateUserProfile(profile);
          }
        });
        
        actions.setLoading(false);
      } else {
        if (profileUnsubscribe) profileUnsubscribe();
        profileUnsubscribe = null;
        actions.setUserId(null);
        actions.setUser(null);
        actions.setAuthenticated(false);
        actions.setLoading(false);
      }
    });
    
    // Initialize authentication
    initAuth();
    
    // Cleanup
    return () => {
      if (unsubscribe) unsubscribe();
      if (profileUnsubscribe) profileUnsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Import pending assessment or startup checklist as a NEW project after signup.
  useEffect(() => {
    const importPendingAsNewProject = async () => {
      if (assessmentImportInProgress.current) return;
      if (!state.isAuthenticated || !state.userId) return;

      const pendingAssessment = getPendingAssessment();
      const pendingChecklist = getPendingStartupChecklist();
      if (!pendingAssessment && !pendingChecklist) return;

      assessmentImportInProgress.current = true;
      try {
        const appId = getAppId();
        const overrides = {};
        let projectName = 'My Restaurant Plan';

        if (pendingAssessment) {
          overrides.freeAssessment = {
            ...pendingAssessment,
            importedAt: new Date().toISOString(),
          };
          if (pendingAssessment.restaurantName?.trim()) {
            projectName = pendingAssessment.restaurantName.trim();
          } else {
            projectName = 'Restaurant Assessment';
          }
        }
        if (pendingChecklist) {
          overrides.startupChecklist = {
            ...pendingChecklist,
            importedAt: new Date().toISOString(),
          };
          if (pendingChecklist.projectName?.trim()) {
            projectName = pendingChecklist.projectName.trim();
          } else if (!pendingAssessment) {
            projectName = 'Restaurant Startup';
          }
        }

        const newDraft = actions.createDraftFromImport(projectName, overrides);
        await dbService.saveDraft(state.userId, appId, newDraft);
        const updatedDrafts = [...state.drafts, newDraft];
        await dbService.saveDraftsMetadata(state.userId, appId, updatedDrafts);

        if (pendingAssessment) clearPendingAssessment();
        if (pendingChecklist) clearPendingStartupChecklist();

        const what = pendingAssessment && pendingChecklist
          ? 'assessment and checklist'
          : pendingAssessment
            ? 'assessment'
            : 'startup checklist';
        actions.showMessage(
          'Project created',
          `Your ${what} has been saved as a new project.`,
          'success'
        );
      } catch (error) {
        // Keep pending data in storage so we can retry on next load.
      } finally {
        assessmentImportInProgress.current = false;
      }
    };

    importPendingAsNewProject();
  }, [state.isAuthenticated, state.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save functionality - debounced save on data changes
  useEffect(() => {
    // Only auto-save if user is authenticated and has a current draft
    if (!state.isAuthenticated || !state.userId || !state.currentDraftId) {
      return;
    }

    // Don't auto-save if currently saving or just saved
    if (state.saveStatus === 'saving') {
      return;
    }

    // Set up debounced auto-save (2 seconds after last change)
    const autoSaveTimer = setTimeout(() => {
      // Only save if there are actual changes (not on initial load)
      if (state.businessPlan || state.financialData || state.vendors) {
        actions.saveData(true); // Silent save for auto-save
      }
    }, 2000); // 2 second delay

    // Cleanup timer on unmount or when dependencies change
    return () => {
      clearTimeout(autoSaveTimer);
    };
  }, [
    state.businessPlan,
    state.financialData,
    state.vendors,
    state.isAuthenticated,
    state.userId,
    state.currentDraftId,
    state.saveStatus,
    actions
  ]);

  return (
    <AppContext.Provider value={{ state, actions }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

/** Initial state shape for tests and defaults */
export const getInitialAppState = () => ({ ...initialState });

export default AppContext; 